import { CopyOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Row, Select } from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import debounce from "lodash.debounce";
import {
  AIR_HOLE_TYPE_LIST_ENDPOINT,
  ALL_SALES_CONTRACT_LIST,
  ALL_SUB_STYLES,
  AU_FORMULA_LIST_ENDPOINT,
  BOOKING_CARTON_GET_GARMENT_QTY,
  CARTON_TYPE_LIST_ENDPOINT,
  GET_ITEM_CATEGORIES_API,
  GET_STYLE_LIST_ENDPOINT,
  GUSSET_TYPE_LIST_ENDPOINT,
  PLY_LIST_ENDPOINT,
  PO_MAP_NEW_ENDPOINT,
  POLY_TYPE_LIST_ENDPOINT,
  SALES_CONTRACT_LIST,
  SUPPORTING_MATERIAL_TYPE_LIST_ENDPOINT,
  UNIT_LIST_ENDPOINT,
  getItemCategoryAssignedUnitsUrl,
} from "../../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const WRAP_HEADER_STYLE = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.2,
};

/** Decimal precision for Rate, Rate per Pcs/Dzn, and Total Cost in breakdown */
const BREAKDOWN_DECIMALS = 4;

const roundBreakdownMoney = (n) => Number(Number(n || 0).toFixed(BREAKDOWN_DECIMALS));

const formatBreakdownCell = (v) => {
  if (v == null || v === "") return "";
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return num.toFixed(BREAKDOWN_DECIMALS);
};

const DEFAULT_SIZE_ROW_KEY = "default";

/** Treat 0 as unsaved — copied/new lines use id 0 until persisted. */
const getPersistedBreakdownLineId = (sizeDetail) => {
  const id = sizeDetail?.id;
  return id != null && Number(id) !== 0 ? id : null;
};

const breakdownRowSizeKey = (rowIndex, sizeId) =>
  `${rowIndex}-${sizeId == null ? DEFAULT_SIZE_ROW_KEY : sizeId}`;

const dedupeTargetKeys = (keys) => [...new Set((keys || []).filter(Boolean))];

/** One option per breakdown size line — dedupe by API breakdown id, then row+size key. */
const dedupeSizeDetailsForLink = (sizeDetails) => {
  const seen = new Set();
  return (sizeDetails || []).filter((sd) => {
    const persistedId = getPersistedBreakdownLineId(sd);
    const dedupeKey = persistedId != null ? `id:${persistedId}` : `size:${sd?.size_id ?? "__null__"}`;
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
};

const collectCartonBreakdownTargetKeys = (breakdownDetails, rowIsPolyBagFn) => {
  const keys = [];
  const seenKeys = new Set();
  const seenBreakdownIds = new Set();
  (breakdownDetails || []).forEach((row, rowIndex) => {
    if (rowIsPolyBagFn(row)) return;
    dedupeSizeDetailsForLink(row.size_details).forEach((sd) => {
      const rowKey = breakdownRowSizeKey(rowIndex, sd.size_id);
      const persistedId = getPersistedBreakdownLineId(sd);
      if (persistedId != null) {
        if (seenBreakdownIds.has(persistedId)) return;
        seenBreakdownIds.add(persistedId);
      } else if (seenKeys.has(rowKey)) {
        return;
      }
      seenKeys.add(rowKey);
      keys.push(rowKey);
    });
  });
  return keys;
};

const findTargetKeyForBreakdownRelation = (breakdownDetails, rel, rowIsPolyBagFn) => {
  const bid = rel?.booking_carton_break_down_id;
  if (bid == null) return null;
  for (let rowIndex = 0; rowIndex < (breakdownDetails || []).length; rowIndex += 1) {
    const row = breakdownDetails[rowIndex];
    if (rowIsPolyBagFn(row)) continue;
    for (const sd of dedupeSizeDetailsForLink(row.size_details)) {
      const lineId = sd.id ?? row.id ?? row.booking_carton_break_down_id;
      if (lineId !== bid) continue;
      if (rel.size_id != null && !sizesMatch(rel.size_id, sd.size_id)) continue;
      return breakdownRowSizeKey(rowIndex, sd.size_id);
    }
  }
  return null;
};

const resolveBreakdownLineIdForTargetKey = (breakdownDetails, key) => {
  const { rowIndex, sizeId } = parseBreakdownRowSizeKey(key);
  const row = breakdownDetails?.[rowIndex];
  if (!row) return null;
  const sizeDetail = (row.size_details || []).find((sd) => sizesMatch(sd.size_id, sizeId));
  return sizeDetail?.id ?? row?.id ?? row?.booking_carton_break_down_id ?? null;
};

const buildCartonLineSerialMap = (breakdownDetails, rowIsPolyBagFn, rowIsCartonBoxFn) => {
  const serialByLineId = new Map();
  const assignSerialsForSection = (cartonBoxOnly) => {
    let serial = 0;
    (breakdownDetails || []).forEach((row) => {
      if (rowIsPolyBagFn(row)) return;
      if (cartonBoxOnly && !rowIsCartonBoxFn(row)) return;
      if (!cartonBoxOnly && rowIsCartonBoxFn(row)) return;
      dedupeSizeDetailsForLink(row.size_details).forEach((sd) => {
        const lineId = sd.id ?? row.id ?? row.booking_carton_break_down_id;
        if (lineId == null) return;
        serial += 1;
        if (!serialByLineId.has(lineId)) {
          serialByLineId.set(lineId, serial);
        }
      });
    });
  };
  assignSerialsForSection(true);
  assignSerialsForSection(false);
  return serialByLineId;
};

const buildSupportingMaterialBreakdownPayload = ({
  targetKeys,
  breakdownDetails,
  editingMaterial,
  editingRelations,
}) => {
  const selectedLineIds = dedupeTargetKeys(
    targetKeys.map((key) => resolveBreakdownLineIdForTargetKey(breakdownDetails, key)).filter((id) => id != null),
  );

  const createEntry = (relationId, lineId, isDelete = 0) => ({
    id: relationId || 0,
    booking_carton_break_down_id: lineId,
    is_delete: isDelete,
  });

  if (editingMaterial) {
    const rels = editingRelations || [];
    const selectedSet = new Set(selectedLineIds);
    const breakdowns = [];
    rels.forEach((rel) => {
      const bid = rel.booking_carton_break_down_id;
      if (bid == null) return;
      if (selectedSet.has(bid)) {
        breakdowns.push(createEntry(rel.id, bid, 0));
        selectedSet.delete(bid);
      } else {
        breakdowns.push(createEntry(rel.id, bid, 1));
      }
    });
    selectedSet.forEach((bid) => {
      breakdowns.push(createEntry(0, bid, 0));
    });
    return breakdowns;
  }

  return selectedLineIds.map((bid) => createEntry(0, bid, 0));
};

const parseBreakdownRowSizeKey = (key) => {
  const idx = key.indexOf("-");
  if (idx === -1) return { rowIndex: 0, sizeId: null };
  const rowIndex = parseInt(key.slice(0, idx), 10);
  const sizePart = key.slice(idx + 1);
  if (sizePart === DEFAULT_SIZE_ROW_KEY) return { rowIndex: Number.isNaN(rowIndex) ? 0 : rowIndex, sizeId: null };
  const parsed = Number(sizePart);
  return { rowIndex: Number.isNaN(rowIndex) ? 0 : rowIndex, sizeId: Number.isNaN(parsed) ? null : parsed };
};

const sizesMatch = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const getSizeLabelFromOptions = (sizeId, availableSizes) => {
  if (sizeId == null) return "—";
  const sizeInfo = (availableSizes || []).find((s) => sizesMatch(s.value, sizeId));
  return sizeInfo?.label || `Size ${sizeId}`;
};

const createEmptySizeDetailRow = (sizeId, availableSizes = []) => ({
  size_id: sizeId,
  size_name: getSizeLabelFromOptions(sizeId, availableSizes),
  item: "",
  upc: "",
  garment_qty: 0,
  qty_pcs_dzn: 0,
  pack_qty: 1,
  carton_qty: 0,
  rate: 0,
  rate_pcs: 0,
  rate_dzn: 0,
  rate_pcs_dzn: 0,
  total_cost: 0,
  additional_charge_per_pcs: 0,
  additional_cost: 0,
  grand_total: 0,
  carton_total_qty: 0,
});

/** Copy qty/rate/total fields from a prior size line when adding a new size row. */
const copySizeDetailFromPrevious = (previous, sizeId, availableSizes = []) => {
  const base = createEmptySizeDetailRow(sizeId, availableSizes);
  if (!previous) return base;
  return {
    ...base,
    item: previous.item ?? base.item,
    upc: previous.upc ?? base.upc,
    garment_qty: previous.garment_qty ?? base.garment_qty,
    qty_pcs_dzn: previous.qty_pcs_dzn ?? base.qty_pcs_dzn,
    pack_qty: previous.pack_qty ?? base.pack_qty,
    carton_qty: previous.carton_qty ?? base.carton_qty,
    rate: previous.rate ?? base.rate,
    rate_pcs: previous.rate_pcs ?? base.rate_pcs,
    rate_dzn: previous.rate_dzn ?? base.rate_dzn,
    rate_pcs_dzn: previous.rate_pcs_dzn ?? base.rate_pcs_dzn,
    additional_charge_per_pcs: previous.additional_charge_per_pcs ?? base.additional_charge_per_pcs,
    additional_cost: previous.additional_cost ?? base.additional_cost,
    total_cost: previous.total_cost ?? base.total_cost,
    grand_total: previous.grand_total ?? base.grand_total,
    carton_total_qty: previous.carton_total_qty ?? base.carton_total_qty,
    ...(previous.manual_carton_qty != null && { manual_carton_qty: previous.manual_carton_qty }),
    ...(previous.manual_poly_rate != null && { manual_poly_rate: previous.manual_poly_rate }),
  };
};

const initialRow = {
  selected_sizes: [],
  po: undefined,
  shipment_date: undefined,
  style: undefined,
  sub_style: undefined,
  sub_style_id: undefined,
  brand: undefined,
  style_description: undefined,
  color_code: undefined,
  item_type: undefined,
  carton_type: undefined,
  ply: undefined,
  group: undefined,
  poly_type: undefined,
  flap: undefined,
  flap_length: 0,
  flap_width: 0,
  gusset: undefined,
  gusset_type: undefined,
  gusset_size: 0,
  thickness: 0,
  poly_print_color: undefined,
  air_hole: undefined,
  hanger_cut: undefined,
  hanger_cut_style: undefined,
  hanger_cut_size: undefined,
  hanger_gap_size: 0,
  poly_perforation: undefined,
  poly_total_qty: 0,
  note: undefined,
  item_no: undefined,
  artikel_nr: undefined,
   model_nr: undefined,
  order_nr: undefined,
  farbe: undefined,
  garment_qty: 0,
  qty_pcs_dzn: 0,
  pack_qty: 1,
  carton_total_qty: 0,
  carton_formula_factor: 1.03,
  carton_formula_add: 2,
  l: 0,
  w: 0,
  h: 0,
  au: undefined,
  rate: 0,
  rate_pcs_doz: 0,
  total_cost: 0,
  additional_cost: 0,
  grand_total: 0,
  size_details: [],
};

const isCartonBoxItemType = (itemType, getLabel) => {
  const label = (getLabel ? getLabel(itemType) : String(itemType || "")).toLowerCase();
  return label.includes("carton box");
};

const isPolyItemType = (itemType, getLabel) => {
  const label = (getLabel ? getLabel(itemType) : String(itemType || "")).toLowerCase();
  return label.includes("poly");
};

/** Clear fields not used for Carton Box (UI + API). */
export function stripCartonBoxExcludedFields(row, resolveItemTypeLabel = () => row?.item_type_name || "") {
  if (!isCartonBoxItemType(row?.item_type, resolveItemTypeLabel)) {
    return row;
  }
  const sizeDetails = (row.size_details || []).map((sd) => {
    const totalCost = Number(sd.total_cost ?? 0);
    return {
      ...sd,
      item: null,
      upc: null,
      qty_pcs_dzn: null,
      grand_total: roundBreakdownMoney(totalCost + Number(sd.additional_cost ?? 0)),
    };
  });
  return {
    ...row,
    unit_id: null,
    unit: null,
    poly_type_id: null,
    poly_type: null,
    gusset_type_id: null,
    gusset_type: null,
    air_hole_type_id: null,
    air_hole: null,
    fabre_color_id: null,
    flap: null,
    flap_length: null,
    gusset: null,
    gusset_size: null,
    thickness: null,
    poly_print: null,
    poly_print_color: null,
    hanger_cut: null,
    hanger_cut_style: null,
    hanger_cut_size: null,
    hanger_gap_size: null,
    poly_perforation: null,
    poly_total_qty: 0,
    model_nr: null,
    order_nr: null,
    grand_total: roundBreakdownMoney(
      sizeDetails.reduce((sum, sd) => sum + Number(sd.grand_total ?? (sd.total_cost || 0)), 0),
    ),
    qty_pcs_dzn: null,
    quantity: null,
    size_details: sizeDetails,
  };
}

/** Clear fields not used for Poly Bag (UI + API). */
export function stripPolyExcludedFields(row, resolveItemTypeLabel = () => row?.item_type_name || "") {
  if (!isPolyItemType(row?.item_type, resolveItemTypeLabel)) {
    return row;
  }
  const sizeDetails = (row.size_details || []).map((sd) => {
    const lineTotal = Number(sd.total_cost ?? 0);
    const polyLineQty = Number(sd.qty_pcs_dzn ?? sd.carton_qty ?? 0);
    return {
      ...sd,
      item: null,
      upc: null,
      qty_pcs_dzn: polyLineQty,
      carton_qty: polyLineQty,
      carton_total_qty: polyLineQty,
      grand_total: roundBreakdownMoney(lineTotal + Number(sd.additional_cost ?? 0)),
    };
  });
  const polyTotalQty = sizeDetails.reduce((sum, sd) => sum + Number(sd.qty_pcs_dzn ?? 0), 0);
  return {
    ...row,
    shipment_date: null,
    sub_style: null,
    sub_style_id: null,
    brand: null,
    carton_type_id: null,
    carton_type: null,
    ply_id: null,
    ply: null,
    group: null,
    note: null,
    item_no: null,
    artikel_nr: null,
    article_no: null,
    farbe: null,
    fabre_color_id: null,
    h: 0,
    model_nr: null,
    order_nr: null,
    hanger_cut_size: null,
    grand_total: roundBreakdownMoney(
      sizeDetails.reduce((sum, sd) => sum + Number(sd.grand_total ?? (sd.total_cost || 0)), 0),
    ),
    poly_total_qty: polyTotalQty,
    size_details: sizeDetails,
  };
}

const CARTON_ROW_FIELD_RESETS = {
  carton_type: undefined,
  carton_type_id: undefined,
  ply: undefined,
  ply_id: undefined,
  group: undefined,
  note: undefined,
  item_no: undefined,
  artikel_nr: undefined,
  article_no: undefined,
  farbe: undefined,
  fabre_color_id: undefined,
  hanger_cut_size: undefined,
};

const POLY_ROW_FIELD_RESETS = {
  poly_type: undefined,
  poly_type_id: undefined,
  flap: undefined,
  flap_length: undefined,
  flap_width: undefined,
  gusset: undefined,
  gusset_type: undefined,
  gusset_type_id: undefined,
  gusset_size: undefined,
  thickness: undefined,
  poly_print_color: undefined,
  poly_print: undefined,
  air_hole: undefined,
  air_hole_type_id: undefined,
  hanger_cut: undefined,
  hanger_cut_style: undefined,
  hanger_gap_size: undefined,
  poly_perforation: undefined,
  poly_total_qty: 0,
  additional_length: undefined,
  additional_width: undefined,
  unit: undefined,
  unit_id: undefined,
  model_nr: undefined,
  order_nr: undefined,
  shipment_date: undefined,
  sub_style: undefined,
  sub_style_id: undefined,
  brand: undefined,
};

const DIMENSION_AND_RATE_RESETS = {
  l: 0,
  w: 0,
  h: 0,
  rate: 0,
  rate_pcs_doz: 0,
  total_cost: 0,
  additional_cost: 0,
  grand_total: 0,
  qty_pcs_dzn: 0,
  quantity: 0,
  carton_total_qty: 0,
};

const resetSizeDetailFieldsForItemType = (sizeDetails, nextItemType, resolveItemTypeLabel) => {
  const cartonBox = isCartonBoxItemType(nextItemType, resolveItemTypeLabel);
  const polyBag = isPolyItemType(nextItemType, resolveItemTypeLabel);
  return (sizeDetails || []).map((sd) => {
    const pricingReset = {
      rate: 0,
      rate_pcs: 0,
      rate_dzn: 0,
      rate_pcs_dzn: 0,
      total_cost: 0,
      additional_charge_per_pcs: 0,
      additional_cost: 0,
      grand_total: 0,
      manual_carton_qty: false,
      manual_poly_rate: false,
    };
    if (cartonBox) {
      return {
        ...sd,
        ...pricingReset,
        item: null,
        upc: null,
        qty_pcs_dzn: null,
      };
    }
    if (polyBag) {
      return {
        ...sd,
        ...pricingReset,
        item: null,
        upc: null,
        qty_pcs_dzn: 0,
        carton_qty: 0,
        carton_total_qty: 0,
      };
    }
    return { ...sd, ...pricingReset };
  });
};

/** Clear type-specific and calculated fields when item type changes in the drawer. */
const applyItemTypeFieldResets = (row, nextItemType, resolveItemTypeLabel) => {
  const cartonBox = isCartonBoxItemType(nextItemType, resolveItemTypeLabel);
  const polyBag = isPolyItemType(nextItemType, resolveItemTypeLabel);
  let next = {
    ...row,
    ...DIMENSION_AND_RATE_RESETS,
    size_details: resetSizeDetailFieldsForItemType(row.size_details, nextItemType, resolveItemTypeLabel),
  };
  if (cartonBox) {
    next = { ...next, ...POLY_ROW_FIELD_RESETS };
  } else if (polyBag) {
    next = { ...next, ...CARTON_ROW_FIELD_RESETS };
  } else {
    next = { ...next, ...CARTON_ROW_FIELD_RESETS, ...POLY_ROW_FIELD_RESETS };
  }
  return next;
};

/** Carton / poly total qty for parent table cells (drawer stores carton_qty per size line). */
const getSizeCartonTotalQty = (row, sizeDetail, wantPoly) => {
  const sd = sizeDetail || {};
  if (wantPoly) {
    return sd.carton_qty ?? sd.qty_pcs_dzn ?? sd.carton_total_qty ?? 0;
  }
  return sd.carton_qty ?? sd.carton_total_qty ?? row?.carton_total_qty ?? 0;
};

const STICKY_HEADER_COL_KEYS = new Set(["po", "shipment_date", "style"]);

const CHECKBOX_COL_WIDTH = 40;
const SL_COL_WIDTH = 50;

const STICKY_PINNED_BG = "#ffffff";
const BREAKDOWN_ROW_BG_ODD = "#f8f8f8";
const BREAKDOWN_ROW_BG_EVEN = "#e7ebec";
const BREAKDOWN_HEADER_BG = "#cdffcd";

const STICKY_BODY_Z_BASE = 1000;
const STICKY_HEADER_Z_BASE = 2000;

const getBreakdownRowBg = (serial) => (serial % 2 === 0 ? BREAKDOWN_ROW_BG_EVEN : BREAKDOWN_ROW_BG_ODD);

const QTY_SUMMARY_COL_KEYS = new Set([
  "garment_qty",
  "qty_pcs_dzn",
  "carton_total_qty",
  "poly_total_qty_row",
]);

const COST_SUMMARY_COL_KEYS = new Set(["additional_cost", "total_cost", "grand_total"]);

const applyStickyThroughStyle = (columns, showSelectColumn) => {
  let left = (showSelectColumn ? CHECKBOX_COL_WIDTH : 0) + SL_COL_WIDTH;
  let stickyOrder = 2;
  return columns.map((col) => {
    if (!STICKY_HEADER_COL_KEYS.has(col.key)) {
      return { ...col, isScrollColumn: true };
    }
    const stickyLeft = left;
    left += col.minWidth || 100;
    const order = stickyOrder++;
    return { ...col, stickyLeft, stickyOrder: order, isLastSticky: col.key === "style" };
  });
};

const stickyPinnedCellStyle = ({
  left,
  zIndex,
  isLastSticky = false,
  background = STICKY_PINNED_BG,
  extra = {},
}) => ({
  position: "sticky",
  left,
  zIndex,
  backgroundColor: background,
  background,
  backgroundClip: "padding-box",
  transform: "translateZ(0)",
  boxShadow: isLastSticky ? "6px 0 10px -4px rgba(0, 0, 0, 0.2)" : undefined,
  ...extra,
});

const stickyColStyle = (col, { isHeader = false, extra = {} } = {}) => {
  if (col.stickyLeft == null) {
    return scrollableCellStyle(col, isHeader ? BREAKDOWN_HEADER_BG : BREAKDOWN_ROW_BG_ODD, extra);
  }
  const zIndex = (isHeader ? STICKY_HEADER_Z_BASE : STICKY_BODY_Z_BASE) + (col.stickyOrder ?? 0);
  return {
    ...(col.cellStyle || {}),
    ...stickyPinnedCellStyle({
      left: col.stickyLeft,
      zIndex,
      isLastSticky: col.isLastSticky,
      background: isHeader ? BREAKDOWN_HEADER_BG : STICKY_PINNED_BG,
      extra,
    }),
  };
};

const scrollableCellStyle = (col, rowBackground, extra = {}) => ({
  ...(col.cellStyle || {}),
  backgroundColor: rowBackground,
  background: rowBackground,
  ...extra,
});

const computeSectionGrandTotals = (fields, breakdownRows, { wantPoly, cartonBoxOnly, rowIsPolyBag, rowIsCartonBox }) => {
  const totals = {
    garment_qty: 0,
    qty_pcs_dzn: 0,
    carton_total_qty: 0,
    poly_total_qty_row: 0,
    additional_cost: 0,
    total_cost: 0,
    grand_total: 0,
  };
  const polyRowQtySeen = new Set();

  fields.forEach((field, index) => {
    const rowData = breakdownRows[index] || {};
    if (rowIsPolyBag(rowData) !== wantPoly) return;
    if (cartonBoxOnly && !rowIsCartonBox(rowData)) return;
    if (!wantPoly && !cartonBoxOnly && rowIsCartonBox(rowData)) return;
    const rowSizes = rowData.size_details || [];
    if (!rowSizes.length) return;

    if (wantPoly && !polyRowQtySeen.has(index)) {
      totals.poly_total_qty_row += Number(rowData.poly_total_qty) || 0;
      polyRowQtySeen.add(index);
    }

    rowSizes.forEach((sd) => {
      if (!wantPoly) {
        totals.garment_qty += Number(sd.garment_qty) || 0;
      }
      totals.carton_total_qty += Number(getSizeCartonTotalQty(rowData, sd, wantPoly)) || 0;
      totals.total_cost += Number(sd.total_cost) || 0;
      totals.additional_cost += Number(sd.additional_cost) || 0;
      totals.grand_total += Number(sd.grand_total) || 0;
      if (!cartonBoxOnly && !wantPoly) {
        totals.qty_pcs_dzn += Number(sd.qty_pcs_dzn) || 0;
      }
    });
  });

  return totals;
};

const renderSectionGrandTotalCell = (col, totals) => {
  if (col.key === "item_type") {
    return <strong>Grand Total</strong>;
  }
  if (QTY_SUMMARY_COL_KEYS.has(col.key)) {
    const value = totals[col.key] ?? 0;
    return <strong>{col.key === "garment_qty" || col.key === "qty_pcs_dzn" ? value : formatBreakdownCell(value)}</strong>;
  }
  if (COST_SUMMARY_COL_KEYS.has(col.key)) {
    return <strong>{formatBreakdownCell(totals[col.key] ?? 0)}</strong>;
  }
  return "";
};

/** Fields cleared on copied rows — user re-selects sales contract to populate dropdowns. */
const COPY_RESET_CONTRACT_FIELDS = {
  sales_contract_id: undefined,
  po: undefined,
  style: undefined,
  style_id: undefined,
  sub_style_id: undefined,
  sub_style: undefined,
  brand: undefined,
  style_description: undefined,
  shipment_date: undefined,
  color_code: undefined,
};

/** Duplicate a breakdown row for insert; reset persisted ids so edit save creates new lines. */
const cloneBreakdownRowForCopy = (sourceRow) => {
  if (!sourceRow) return sourceRow;
  return {
    ...sourceRow,
    id: 0,
    booking_carton_break_down_id: 0,
    sales_contract_id: null,
    sales_contract_ref: null,
    po: null,
    po_map_id: null,
    po_map: null,
    style: null,
    style_id: null,
    style_ref: null,
    style_description: null,
    sub_style_id: null,
    sub_style: null,
    brand: null,
    shipment_date: null,
    color_code: null,
    farbe: null,
    size_details: (sourceRow.size_details || []).map((sd) => ({
      ...sd,
      id: 0,
    })),
  };
};

/** Normalize drawer output so main table + API payload get consistent IDs and numeric defaults */
function normalizeDrawerRowValues(values, resolveItemTypeLabel = () => "") {
  const v = { ...values };
  const articleNo = v.article_no ?? v.artikel_nr ?? null;
  const carton_type_id = v.carton_type_id ?? v.carton_type ?? null;
  const ply_id = v.ply_id ?? v.ply ?? null;
  const poly_type_id = v.poly_type_id ?? v.poly_type ?? null;
  const gusset_type_id =
    v.gusset_type_id ?? (Array.isArray(v.gusset_type) ? v.gusset_type?.[0] : v.gusset_type) ?? null;
  const air_hole_type_id = v.air_hole_type_id ?? v.air_hole ?? null;
  /** Prefer `au` (drawer Select) over `au_formula_id` so a changed A/U is not overridden by a stale id */
  const au_formula_id = v.au ?? v.au_formula_id ?? null;
  const unit_id = v.unit_id ?? v.unit ?? null;
  const polyPrint = v.poly_print ?? v.poly_print_color ?? null;
  const cartonBox = isCartonBoxItemType(v.item_type, resolveItemTypeLabel);
  const polyBag = isPolyItemType(v.item_type, resolveItemTypeLabel);
  const sizeDetails = (v.size_details || []).map((sd) => {
    const lineTotal = Number(sd.total_cost ?? 0);
    const additionalCost = Number(sd.additional_cost ?? 0);
    const charge = Number(sd.additional_charge_per_pcs ?? 0);
    const polyLineQty = Number(sd.qty_pcs_dzn ?? sd.carton_qty ?? 0);
    const cartonQty = polyBag ? polyLineQty : Number(sd.carton_qty ?? sd.carton_total_qty ?? 0);
    const computedAdditional =
      charge > 0
        ? polyBag
          ? roundBreakdownMoney((cartonQty / 12) * charge)
          : roundBreakdownMoney(cartonQty * charge)
        : additionalCost;
    const ratePcs = Number(sd.rate_pcs ?? sd.rate_pcs_dzn ?? 0);
    const rateDzn = polyBag
      ? roundBreakdownMoney(ratePcs * 12 + charge)
      : sd.rate_dzn ?? roundBreakdownMoney(ratePcs * 12);
    return {
      ...sd,
      garment_qty: sd.garment_qty ?? 0,
      qty_pcs_dzn: cartonBox ? null : polyBag ? polyLineQty : sd.qty_pcs_dzn ?? 0,
      pack_qty: sd.pack_qty ?? 1,
      carton_qty: cartonQty,
      carton_total_qty: cartonQty,
      item: cartonBox || polyBag ? null : sd.item ?? null,
      upc: cartonBox || polyBag ? null : sd.upc ?? null,
      rate: sd.rate ?? 0,
      rate_pcs: ratePcs,
      rate_dzn: rateDzn,
      rate_pcs_dzn: sd.rate_pcs_dzn ?? sd.rate_pcs ?? 0,
      total_cost: lineTotal,
      additional_charge_per_pcs: charge,
      additional_cost: computedAdditional,
      grand_total: roundBreakdownMoney(lineTotal + computedAdditional),
    };
  });
  const subtotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.total_cost) || 0), 0);
  const additionalCost = sizeDetails.reduce((sum, sd) => sum + (Number(sd.additional_cost) || 0), 0);
  const grandTotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.grand_total) || 0), 0);
  const cartonTotalQty = sizeDetails.reduce((sum, sd) => sum + Number(sd.carton_qty || 0), 0);
  const polyAdditionalLength = polyBag
    ? Number(v.additional_length ?? Number(v.flap_length ?? 0) / 2)
    : null;
  const polyAdditionalWidth = polyBag
    ? Number(v.additional_width ?? Number(v.flap_width ?? 0) / 2)
    : null;
  const base = {
    ...v,
    sales_contract_id: v.sales_contract_id ?? null,
    item_type_name: resolveItemTypeLabel(v.item_type),
    additional_length: polyBag ? polyAdditionalLength : null,
    additional_width: polyBag ? polyAdditionalWidth : null,
    additional_cost: roundBreakdownMoney(additionalCost),
    total_cost: roundBreakdownMoney(subtotal),
    grand_total: roundBreakdownMoney(grandTotal),
    carton_total_qty: cartonTotalQty,
    article_no: articleNo,
    artikel_nr: v.artikel_nr ?? articleNo,
    model_nr: resolveItemTypeLabel(v.item_type).toLowerCase().includes("carton") ? null : v.model_nr ?? null,
    order_nr: resolveItemTypeLabel(v.item_type).toLowerCase().includes("carton") ? null : v.order_nr ?? null,
    carton_type_id,
    carton_type: carton_type_id,
    ply_id,
    ply: ply_id,
    poly_type_id,
    poly_type: poly_type_id,
    gusset_type_id,
    gusset_type: gusset_type_id,
    air_hole_type_id,
    air_hole: air_hole_type_id,
    au_formula_id,
    au: au_formula_id,
    unit_id,
    unit: unit_id,
    sub_style_id: v.sub_style_id ?? null,
    sub_style: v.sub_style ?? undefined,
    poly_print: polyPrint,
    poly_print_color: v.poly_print_color ?? polyPrint,
    poly_total_qty: polyBag
      ? sizeDetails.reduce((sum, sd) => sum + Number(sd.qty_pcs_dzn ?? 0), 0)
      : v.poly_total_qty ?? 0,
    hanger_cut_size: polyBag ? null : v.hanger_cut_size ?? null,
    l: v.l ?? 0,
    w: v.w ?? 0,
    h: polyBag ? 0 : v.h ?? 0,
    size_details: sizeDetails,
    qty_pcs_dzn: cartonBox ? null : v.qty_pcs_dzn ?? null,
    quantity: cartonBox ? null : v.quantity ?? v.qty_pcs_dzn ?? null,
  };
  if (cartonBox) {
    return stripCartonBoxExcludedFields(base, resolveItemTypeLabel);
  }
  if (polyBag) {
    return stripPolyExcludedFields(base, resolveItemTypeLabel);
  }
  return base;
}

export default function BreakdownTable({
  form,
  viewMode,
  buyerId,
  isEdit,
  bookingCartonId,
  onSaveSupportingMaterial,
  supportingMaterials,
}) {
  const [drawerSelectedSizes, setDrawerSelectedSizes] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [poList, setPoList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [salesContractOptions, setSalesContractOptions] = useState([]);
  const [drawerSalesContractData, setDrawerSalesContractData] = useState(null);
  const [polyUnitOptions, setPolyUnitOptions] = useState([]);
  const [itemTypeOptions, setItemTypeOptions] = useState([]);
  const [drawerStyleOptions, setDrawerStyleOptions] = useState([]);
  const [subStyleSearchOptions, setSubStyleSearchOptions] = useState([]);
  const [drawerPoOptions, setDrawerPoOptions] = useState([]);
  const styleLabelCacheRef = useRef(new Map());
  const subStyleLabelCacheRef = useRef(new Map());
  const [rowDrawerOpen, setRowDrawerOpen] = useState(false);
  const [rowDrawerIndex, setRowDrawerIndex] = useState(null);
  /** Synced when opening drawer so size details render on first paint (selectedSizes updates async) */
  const [drawerSizeIds, setDrawerSizeIds] = useState([]);
  const [rowDrawerForm] = Form.useForm();
  const drawerItemType = Form.useWatch("item_type", rowDrawerForm);
  const drawerUnit = Form.useWatch("unit", rowDrawerForm);
  const drawerSalesContractId = Form.useWatch("sales_contract_id", rowDrawerForm);
  /** Re-render parent breakdown tables when drawer saves nested size_details. */
  const breakdownDetailsWatch = Form.useWatch("breakdown_details", form);
  const [breakdownTableRevision, setBreakdownTableRevision] = useState(0);
  const bumpBreakdownTable = () => setBreakdownTableRevision((rev) => rev + 1);
  const getBreakdownRowsSnapshot = useCallback(() => {
    const rows = form.getFieldValue("breakdown_details");
    if (Array.isArray(rows)) return rows;
    return Array.isArray(breakdownDetailsWatch) ? breakdownDetailsWatch : [];
  }, [form, breakdownDetailsWatch]);
  const [materialModalOpen, setMaterialModalOpen] = useState(false);
  const [materialForm] = Form.useForm();
  const [selectedStyles, setSelectedStyles] = useState({}); // Track selected styles per row index
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // Track checked breakdown rows
  const [materialDetailsList, setMaterialDetailsList] = useState([]); // Stored material entries
  const formListAddRef = React.useRef(null); // Store Form.List add function
  const isSubmittingDrawerRef = React.useRef(false); // Prevent double Save from drawer
  const rowDrawerIndexRef = React.useRef(null);
  const [apiSupportingMaterials, setApiSupportingMaterials] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingRelations, setEditingRelations] = useState([]);

  // Dropdown option lists (loaded from API with `name` search)
  const [cartonTypeOptions, setCartonTypeOptions] = useState([]);
  const [plyOptions, setPlyOptions] = useState([]);
  const [polyTypeOptions, setPolyTypeOptions] = useState([]);
  const [gussetTypeOptions, setGussetTypeOptions] = useState([]);
  const [airHoleTypeOptions, setAirHoleTypeOptions] = useState([]);
  const [auFormulaOptions, setAuFormulaOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [supportingMaterialTypeOptions, setSupportingMaterialTypeOptions] = useState([]);

  useEffect(() => {
    fetchCartonTypes();
    fetchPlies();
    fetchPolyTypes();
    fetchGussetTypes();
    fetchAirHoleTypes();
    fetchAuFormulas();
    fetchUnits();
    fetchSupportingMaterialTypes();
    fetchItemTypes();
  }, []);

  useEffect(() => {
    if (buyerId) {
      fetchSalesContractsForBuyer(buyerId);
    } else {
      setSalesContractOptions([]);
      setAvailableSizes([]);
      setPoList([]);
      setStyleList([]);
      setColorList([]);
      setDrawerSalesContractData(null);
      prefetchedSalesContractIdsRef.current = new Set();
    }
  }, [buyerId]);

  const fetchSalesContractsForBuyer = async (id, search = "") => {
    if (!id) return;
    const res = await getData(ALL_SALES_CONTRACT_LIST, false, {
      buyer_id: id,
      ...(search ? { reference_no: search } : {}),
    });
    if (res?.data?.code === 200 || res?.status === 200) {
      const rows = res?.data?.data || [];
      setSalesContractOptions(
        (Array.isArray(rows) ? rows : []).map((sc) => ({
          value: sc.id,
          label: sc.reference_no ?? `SC ${sc.id}`,
        })),
      );
    }
  };

  const debouncedFetchSalesContracts = useMemo(
    () =>
      debounce((search) => {
        if (buyerId) fetchSalesContractsForBuyer(buyerId, search);
      }, 400),
    [buyerId],
  );

  const prefetchedSalesContractIdsRef = useRef(new Set());

  const mergeUniqueById = (prev, incoming, getId = (item) => item.id) => {
    const merged = [...prev];
    (incoming || []).forEach((item) => {
      const id = getId(item);
      if (id == null) return;
      if (!merged.some((existing) => getId(existing) === id)) {
        merged.push(item);
      }
    });
    return merged;
  };

  const mergeUniqueDrawerOptions = (prev, incoming) => {
    const merged = [...(prev || [])];
    (incoming || []).forEach((item) => {
      if (item?.value == null) return;
      if (!merged.some((existing) => existing.value === item.value)) {
        merged.push(item);
      }
    });
    return merged;
  };

  /** Merge style list entries and enrich with sub_style_map / color_map from sales contract view. */
  const mergeStyleListEntries = (prev, incoming) => {
    const merged = [...prev];
    (incoming || []).forEach((item) => {
      const styleId = item?.style_id;
      if (styleId == null) return;
      const idx = merged.findIndex((existing) => existing.style_id === styleId);
      if (idx === -1) {
        merged.push(item);
        return;
      }
      const existing = merged[idx];
      const existingInfo = existing.style_info || {};
      const incomingInfo = item.style_info || {};
      merged[idx] = {
        ...existing,
        ...item,
        style_id: styleId,
        style_info: {
          ...existingInfo,
          ...incomingInfo,
          style_no: incomingInfo.style_no ?? existingInfo.style_no,
          description: incomingInfo.description ?? existingInfo.description,
          brand_info: incomingInfo.brand_info ?? existingInfo.brand_info,
          sub_style_map: incomingInfo.sub_style_map?.length
            ? incomingInfo.sub_style_map
            : existingInfo.sub_style_map,
          color_map: incomingInfo.color_map?.length ? incomingInfo.color_map : existingInfo.color_map,
        },
      };
    });
    return merged;
  };

  const mergeSalesContractViewData = useCallback((data) => {
    if (!data?.sales_contract) return;
    const sc = data.sales_contract;
    setSalesContractOptions((prev) =>
      prev.some((o) => o.value === sc.id)
        ? prev
        : [{ value: sc.id, label: sc.reference_no ?? `SC ${sc.id}` }, ...prev],
    );
    setAvailableSizes((prev) => {
      const incoming = (sc.size_info || []).map((si) => ({
        value: si.size_id,
        label: si.sizes?.name || `Size ${si.size_id}`,
        position: si.position,
      }));
      const merged = [...prev];
      incoming.forEach((size) => {
        if (!merged.some((existing) => sizesMatch(existing.value, size.value))) {
          merged.push(size);
        }
      });
      return merged.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    });
    setPoList((prev) => mergeUniqueById(prev, data.purchase_orders || []));
    setStyleList((prev) => mergeStyleListEntries(prev, data.styleDetail || []));
    setColorList((prev) => {
      const colors = [...prev];
      data.styleDetail?.forEach((style) => {
        style.style_info?.color_map?.forEach((cm) => {
          if (!colors.find((c) => c.id === cm.color_id)) {
            colors.push({ id: cm.color_id, name: cm.color_info?.name });
          }
        });
      });
      return colors;
    });
  }, []);

  const seedLookupListsFromBreakdownRows = useCallback((rows) => {
    if (!rows?.length) return;
    rows.forEach((row) => {
      if (row.sales_contract_id && row.sales_contract_ref) {
        setSalesContractOptions((prev) =>
          prev.some((o) => o.value === row.sales_contract_id)
            ? prev
            : [
                {
                  value: row.sales_contract_id,
                  label: row.sales_contract_ref.reference_no ?? `SC ${row.sales_contract_id}`,
                },
                ...prev,
              ],
        );
      }
      if (row.po && row.po_map) {
        setPoList((prev) =>
          mergeUniqueById(prev, [
            {
              id: row.po_map.id ?? row.po,
              original_po: row.po_map.original_po,
              internal_po: row.po_map.internal_po,
              delivery_date: row.po_map.delivery_date,
              sales_contract_id: row.sales_contract_id,
            },
          ]),
        );
      }
      if (row.style && row.style_ref) {
        setStyleList((prev) =>
          mergeStyleListEntries(prev, [
            {
              style_id: row.style_id ?? row.style,
              style_info: {
                style_no: row.style_ref.style_no,
                description: row.style_ref.description,
                brand_info: row.style_ref.brand_info,
                sub_style_map: row.style_ref.sub_style_map,
              },
            },
          ]),
        );
      }
      (row.size_details || []).forEach((sd) => {
        if (sd.size_id == null || !sd.size_name) return;
        setAvailableSizes((prev) => {
          if (prev.some((s) => sizesMatch(s.value, sd.size_id))) return prev;
          return [...prev, { value: sd.size_id, label: sd.size_name, position: prev.length }];
        });
      });
    });
  }, []);

  const prefetchSalesContractViews = useCallback(
    async (salesContractIds) => {
      const ids = [...new Set((salesContractIds || []).filter(Boolean))].filter(
        (id) => !prefetchedSalesContractIdsRef.current.has(id),
      );
      if (!ids.length) return;
      await Promise.all(
        ids.map(async (id) => {
          prefetchedSalesContractIdsRef.current.add(id);
          try {
            const response = await getData(`${SALES_CONTRACT_LIST}/${id}/view`);
            mergeSalesContractViewData(response?.data?.data);
          } catch (error) {
            prefetchedSalesContractIdsRef.current.delete(id);
            console.error("Error prefetching sales contract data:", error);
          }
        }),
      );
    },
    [mergeSalesContractViewData],
  );

  useEffect(() => {
    const rows = Array.isArray(breakdownDetailsWatch) ? breakdownDetailsWatch : [];
    if (!rows.length) return;
    seedLookupListsFromBreakdownRows(rows);
    const salesContractIds = rows.map((row) => row.sales_contract_id).filter(Boolean);
    if (salesContractIds.length) {
      prefetchSalesContractViews(salesContractIds);
    }
  }, [breakdownDetailsWatch, seedLookupListsFromBreakdownRows, prefetchSalesContractViews]);

  const cacheStyleOption = (styleId, label) => {
    if (styleId == null || !label) return;
    styleLabelCacheRef.current.set(styleId, label);
  };

  const cacheSubStyleOption = (subStyleId, label) => {
    if (subStyleId == null || !label) return;
    subStyleLabelCacheRef.current.set(subStyleId, label);
  };

  const mapContractStyleToDrawerOption = (styleEntry) => {
    const styleId = styleEntry.style_id;
    const label = styleEntry.style_info?.style_no || `Style ${styleId}`;
    cacheStyleOption(styleId, label);
    return {
      value: styleId,
      label,
      brand: styleEntry.style_info?.brand_info?.name,
      description: styleEntry.style_info?.description,
    };
  };

  const seedDrawerSelectOptionsFromRow = useCallback(
    (rowData) => {
      if (!rowData) return;
      if (rowData.po != null) {
        const poId = rowData.po_map?.id ?? rowData.po;
        const fromList = poList.find((p) => p.id === poId);
        const poEntry = fromList || {
          id: poId,
          original_po: rowData.po_map?.original_po,
          internal_po: rowData.po_map?.internal_po,
          delivery_date: rowData.po_map?.delivery_date,
          sales_contract_id: rowData.sales_contract_id,
        };
        if (poEntry?.id != null) {
          setDrawerPoOptions((prev) => mergeUniqueById(prev, [poEntry]));
        }
      }
      const styleId = rowData.style ?? rowData.style_id;
      if (styleId != null) {
        const label =
          rowData.style_ref?.style_no ||
          styleLabelCacheRef.current.get(styleId) ||
          styleList.find((s) => s.style_id === styleId)?.style_info?.style_no ||
          `Style ${styleId}`;
        cacheStyleOption(styleId, label);
        setDrawerStyleOptions((prev) =>
          mergeUniqueDrawerOptions(prev, [
            {
              value: styleId,
              label,
              brand: rowData.brand ?? rowData.style_ref?.brand_info?.name,
              description: rowData.style_description ?? rowData.style_ref?.description,
            },
          ]),
        );
      }
      if (rowData.sub_style_id != null) {
        const subStyleLabel = rowData.sub_style || `Sub-style ${rowData.sub_style_id}`;
        cacheSubStyleOption(rowData.sub_style_id, subStyleLabel);
        setSubStyleSearchOptions((prev) =>
          mergeUniqueDrawerOptions(prev, [{ value: rowData.sub_style_id, label: subStyleLabel }]),
        );
      }
    },
    [poList, styleList],
  );

  const fetchDrawerSalesContractData = async (salesContractId) => {
    if (!salesContractId) {
      setDrawerSalesContractData(null);
      return null;
    }
    try {
      const response = await getData(`${SALES_CONTRACT_LIST}/${salesContractId}/view`);
      const data = response?.data?.data;
      setDrawerSalesContractData(data);
      mergeSalesContractViewData(data);
      if (data?.purchase_orders?.length) {
        setDrawerPoOptions((prev) => mergeUniqueById(prev, data.purchase_orders));
      }
      if (data?.styleDetail?.length) {
        const styleOpts = data.styleDetail.map(mapContractStyleToDrawerOption);
        setDrawerStyleOptions((prev) => mergeUniqueDrawerOptions(prev, styleOpts));
      }
      const sc = data?.sales_contract;
      if (sc) {
        setSalesContractOptions((prev) => {
          if (prev.some((o) => o.value === sc.id)) return prev;
          return [{ value: sc.id, label: sc.reference_no ?? `SC ${sc.id}` }, ...prev];
        });
        form.setFieldsValue({
          buying_office_id: sc.buying_office_id ?? sc.buying_office_info?.id ?? null,
          sub_contract_factory_id: sc.factory_info?.id ?? sc.first_party ?? null,
        });
      }
      return data;
    } catch (error) {
      console.error("Error fetching sales contract data:", error);
      setDrawerSalesContractData(null);
      return null;
    }
  };

  const fetchDrawerPoList = useCallback(
    async (search = "", salesContractId = null) => {
      const scId = salesContractId ?? rowDrawerForm.getFieldValue("sales_contract_id");
      const contractPos = drawerSalesContractData?.purchase_orders || [];
      const normalizedSearch = String(search || "").trim().toLowerCase();

      const matchesPoSearch = (po) => {
        if (!normalizedSearch) return true;
        const label = String(po.original_po || po.internal_po || "").toLowerCase();
        return label.includes(normalizedSearch);
      };

      const scopePosToSalesContract = (rows) => {
        if (!scId) return rows;
        return (rows || []).filter((p) => p.sales_contract_id == null || p.sales_contract_id === scId);
      };

      if (scId && contractPos.length) {
        const scoped = scopePosToSalesContract(contractPos).filter(matchesPoSearch);
        setDrawerPoOptions((prev) => mergeUniqueById(prev, scoped));
        setPoList((prev) => mergeUniqueById(prev, scoped));
        return;
      }

      const params = {
        per_page: 20,
        ...(normalizedSearch ? { original_po: search.trim() } : {}),
      };
      try {
        const res = await getData(PO_MAP_NEW_ENDPOINT, false, params);
        if (res?.data?.code === 200) {
          let rows = res?.data?.data?.data ?? res?.data?.data ?? [];
          rows = scopePosToSalesContract(rows).filter(matchesPoSearch);
          setDrawerPoOptions((prev) => mergeUniqueById(prev, rows));
          if (rows.length) {
            setPoList((prev) => mergeUniqueById(prev, rows));
          }
        } else {
          setDrawerPoOptions([]);
        }
      } catch (error) {
        console.error("Error fetching PO list:", error);
        setDrawerPoOptions([]);
      }
    },
    [drawerSalesContractData, rowDrawerForm],
  );

  const debouncedFetchDrawerPoList = useMemo(
    () => debounce((search) => fetchDrawerPoList(search), 400),
    [fetchDrawerPoList],
  );

  useEffect(() => {
    if (!rowDrawerOpen) {
      setDrawerPoOptions([]);
      return;
    }
    if (!drawerSalesContractId) {
      setDrawerPoOptions([]);
      return;
    }
    fetchDrawerPoList("", drawerSalesContractId);
  }, [rowDrawerOpen, drawerSalesContractId, drawerSalesContractData, fetchDrawerPoList]);

  useEffect(() => {
    return () => {
      debouncedFetchDrawerPoList.cancel();
    };
  }, [debouncedFetchDrawerPoList]);

  const fetchStylesForDrawer = useCallback(
    async (search = "") => {
      const normalizedSearch = String(search || "").trim().toLowerCase();
      const contractStyles = drawerSalesContractData?.styleDetail || [];

      const matchesStyleSearch = (styleEntry) => {
        if (!normalizedSearch) return true;
        const styleNo = String(styleEntry.style_info?.style_no || "").toLowerCase();
        return styleNo.includes(normalizedSearch);
      };

      if (!normalizedSearch && contractStyles.length) {
        const styleOpts = contractStyles.map(mapContractStyleToDrawerOption);
        setDrawerStyleOptions((prev) => mergeUniqueDrawerOptions(prev, styleOpts));
        return;
      }

      if (normalizedSearch && contractStyles.length) {
        const filtered = contractStyles.filter(matchesStyleSearch).map(mapContractStyleToDrawerOption);
        if (filtered.length) {
          setDrawerStyleOptions((prev) => mergeUniqueDrawerOptions(prev, filtered));
          return;
        }
      }

      try {
        const res = await getData(GET_STYLE_LIST_ENDPOINT, false, {
          per_page: 20,
          ...(normalizedSearch ? { style_no: search.trim() } : {}),
          ...(buyerId ? { buyer_id: buyerId } : {}),
        });
        if (res?.data?.code === 200) {
          const rows = res?.data?.data?.data ?? res?.data?.data ?? [];
          const options = (rows || []).map((style) => {
            cacheStyleOption(style.id, style.style_no);
            return {
              value: style.id,
              label: style.style_no || `Style ${style.id}`,
              brand: style.brand_info?.name ?? style.brand?.name,
              description: style.description,
            };
          });
          setDrawerStyleOptions((prev) => mergeUniqueDrawerOptions(prev, options));
        } else {
          setDrawerStyleOptions((prev) => prev);
        }
      } catch (error) {
        console.error("Error fetching styles:", error);
        setDrawerStyleOptions([]);
      }
    },
    [buyerId, drawerSalesContractData],
  );

  const fetchSubStylesForDrawer = useCallback(async (search = "") => {
    try {
      const res = await getData(ALL_SUB_STYLES, false, {
        per_page: 20,
        page: 1,
        ...(search?.trim() ? { sub_style_no: search.trim() } : {}),
      });
      if (res?.data?.code === 200) {
        const rows = res?.data?.data?.data ?? res?.data?.data ?? [];
        const options = (rows || []).map((item) => {
          cacheSubStyleOption(item.id, item.sub_style_no);
          return {
            value: item.id,
            label: item.sub_style_no || `Sub-style ${item.id}`,
          };
        });
        setSubStyleSearchOptions(options);
      } else {
        setSubStyleSearchOptions([]);
      }
    } catch (error) {
      console.error("Error fetching sub-styles:", error);
      setSubStyleSearchOptions([]);
    }
  }, []);

  const debouncedFetchStylesForDrawer = useMemo(
    () => debounce((search) => fetchStylesForDrawer(search), 400),
    [fetchStylesForDrawer],
  );

  const debouncedFetchSubStylesForDrawer = useMemo(
    () => debounce((search) => fetchSubStylesForDrawer(search), 400),
    [fetchSubStylesForDrawer],
  );

  useEffect(() => {
    if (!rowDrawerOpen) {
      setDrawerStyleOptions([]);
      setSubStyleSearchOptions([]);
      return;
    }
    fetchStylesForDrawer("");
    fetchSubStylesForDrawer("");
  }, [rowDrawerOpen, drawerSalesContractData, drawerSalesContractId, fetchStylesForDrawer, fetchSubStylesForDrawer]);

  useEffect(() => {
    return () => {
      debouncedFetchStylesForDrawer.cancel();
      debouncedFetchSubStylesForDrawer.cancel();
    };
  }, [debouncedFetchStylesForDrawer, debouncedFetchSubStylesForDrawer]);

  const resolveItemCategoryId = useCallback(
    (itemType) => {
      if (itemType == null || itemType === "") return null;
      const matched = itemTypeOptions.find((opt) => opt.value === itemType || opt.label === itemType);
      if (matched) return matched.value;
      const numericId = Number(itemType);
      return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
    },
    [itemTypeOptions],
  );

  const mergeSelectedUnitIntoOptions = useCallback((options, selectedUnitId) => {
    if (selectedUnitId == null || selectedUnitId === "") return options;
    const hasSelected = options.some(
      (opt) => opt.value === selectedUnitId || String(opt.value) === String(selectedUnitId),
    );
    if (hasSelected) return options;
    const fromAllUnits = unitOptions.find(
      (opt) => opt.value === selectedUnitId || String(opt.value) === String(selectedUnitId),
    );
    if (fromAllUnits) return [...options, fromAllUnits];
    return [...options, { value: selectedUnitId, label: String(selectedUnitId) }];
  }, [unitOptions]);

  const loadPolyAssignedUnits = useCallback(
    async (categoryId, selectedUnitId = null) => {
      const resolvedCategoryId = resolveItemCategoryId(categoryId);
      if (!resolvedCategoryId) {
        setPolyUnitOptions(mergeSelectedUnitIntoOptions(unitOptions, selectedUnitId));
        return;
      }
      try {
        const res = await getData(getItemCategoryAssignedUnitsUrl(resolvedCategoryId), false, { update_view: 1 });
        if (res?.data?.code === 200) {
          const maps = res?.data?.data?.item_category_units || [];
          let options = maps
            .map((m) => ({
              value: m.unit?.id ?? m.unit_id,
              label: m.unit?.name,
            }))
            .filter((u) => u.value != null && u.label);
          if (!options.length && unitOptions.length) {
            options = unitOptions;
          }
          setPolyUnitOptions(mergeSelectedUnitIntoOptions(options, selectedUnitId));
        } else {
          setPolyUnitOptions(mergeSelectedUnitIntoOptions(unitOptions, selectedUnitId));
        }
      } catch (error) {
        console.error("Error fetching assigned units:", error);
        setPolyUnitOptions(mergeSelectedUnitIntoOptions(unitOptions, selectedUnitId));
      }
    },
    [mergeSelectedUnitIntoOptions, resolveItemCategoryId, unitOptions],
  );

  useEffect(() => {
    if (Array.isArray(supportingMaterials)) {
      setApiSupportingMaterials(supportingMaterials);
    }
  }, [supportingMaterials]);

  useEffect(() => {
    if (rowDrawerOpen) {
      syncAllDrawerSizeCalculations(rowDrawerForm.getFieldsValue());
    }
  }, [rowDrawerOpen, itemTypeOptions, auFormulaOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // When PO list loads (e.g. edit), fill missing shipment_date from PO delivery_date for table + form
  useEffect(() => {
    if (!poList.length) return;
    const breakdownDetails = form.getFieldValue("breakdown_details") || [];
    let changed = false;
    const next = breakdownDetails.map((row) => {
      if (!row?.po) return row;
      if (row.shipment_date != null && row.shipment_date !== "") return row;
      const po = poList.find((p) => p.id === row.po);
      if (!po?.delivery_date) return row;
      changed = true;
      const m = moment(po.delivery_date);
      return { ...row, shipment_date: m.isValid() ? m : row.shipment_date };
    });
    if (changed) {
      form.setFieldValue("breakdown_details", next);
    }
  }, [poList, form]);

  const mapApiToOptions = (apiResponse) => {
    const paginated = apiResponse?.data?.data;
    const rows = paginated?.data ?? paginated ?? [];
    return (rows || []).map((r) => ({ value: r.id, label: r.name }));
  };

  const fetchCartonTypes = async (name = "") => {
    const res = await getData(CARTON_TYPE_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setCartonTypeOptions(mapApiToOptions(res));
  };
  const fetchPlies = async (name = "") => {
    const res = await getData(PLY_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setPlyOptions(mapApiToOptions(res));
  };
  const fetchPolyTypes = async (name = "") => {
    const res = await getData(POLY_TYPE_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setPolyTypeOptions(mapApiToOptions(res));
  };
  const fetchGussetTypes = async (name = "") => {
    const res = await getData(GUSSET_TYPE_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setGussetTypeOptions(mapApiToOptions(res));
  };
  const fetchAirHoleTypes = async (name = "") => {
    const res = await getData(AIR_HOLE_TYPE_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setAirHoleTypeOptions(mapApiToOptions(res));
  };
  const fetchAuFormulas = async (name = "") => {
    const res = await getData(AU_FORMULA_LIST_ENDPOINT, false, { per_page: 20, ...(name ? { name } : {}) });
    if (res?.data?.code === 200) setAuFormulaOptions(mapApiToOptions(res));
  };
  const fetchUnits = async () => {
    try {
      const res = await getData(UNIT_LIST_ENDPOINT, false, { per_page: 10000, base_unit: "Yes" });
      const rows = res?.data?.data?.data ?? res?.data?.data ?? [];
      const mapped = (rows || []).map((r) => ({ value: r.id, label: r.name }));
      setUnitOptions(mapped);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };
  const fetchSupportingMaterialTypes = async (name = "") => {
    const res = await getData(SUPPORTING_MATERIAL_TYPE_LIST_ENDPOINT, false, {
      per_page: 20,
      ...(name ? { name } : {}),
    });
    if (res?.data?.code === 200) setSupportingMaterialTypeOptions(mapApiToOptions(res));
  };

  const fetchItemTypes = async () => {
    try {
      const res = await getData(GET_ITEM_CATEGORIES_API, false, { use_fabric_interlining_store: "Booking" });
      const rows = res?.data?.data?.data ?? res?.data?.data ?? [];
      const options = [];

      rows.forEach((item) => {
        const name = String(item?.name || "").toLowerCase();
        if (name.includes("carton") && !options.some((opt) => opt.label.toLowerCase().includes("carton"))) {
          options.push({ value: item.id, label: item.name });
        }
        if (name.includes("poly") && !options.some((opt) => opt.label.toLowerCase().includes("poly"))) {
          options.push({ value: item.id, label: item.name });
        }
      });

      if (options.length > 0) {
        setItemTypeOptions(options);
      }
    } catch (error) {
      console.error("Error fetching item types:", error);
    }
  };

  const getItemTypeLabel = (value) => {
    if (value == null || value === "") return "";
    const matchedOption = itemTypeOptions.find((option) => option.value === value || option.label === value);
    if (matchedOption) return matchedOption.label;
    const normalized = String(value).toLowerCase();
    if (normalized.includes("carton")) return "Carton";
    if (normalized.includes("poly")) return "Poly Bag";
    return String(value);
  };

  const rowIsPolyBag = (rowData) => getItemTypeLabel(rowData?.item_type).toLowerCase().includes("poly");
  const rowIsCartonBox = (rowData) => isCartonBoxItemType(rowData?.item_type, getItemTypeLabel);

  /** Carton: Rate/Pcs is always derived from A/U + L/W/H + Rate (per SQM); not user-editable */
  const drawerItemIsCarton = getItemTypeLabel(drawerItemType).toLowerCase().includes("carton");
  const drawerItemIsCartonBox = isCartonBoxItemType(drawerItemType, getItemTypeLabel);
  const drawerItemIsPoly = getItemTypeLabel(drawerItemType).toLowerCase().includes("poly");

  useEffect(() => {
    if (!rowDrawerOpen) {
      setPolyUnitOptions([]);
      return;
    }
    if (!isPolyItemType(drawerItemType, getItemTypeLabel) || isCartonBoxItemType(drawerItemType, getItemTypeLabel)) {
      setPolyUnitOptions([]);
      return;
    }
    const selectedUnit = drawerUnit ?? rowDrawerForm.getFieldValue("unit");
    loadPolyAssignedUnits(drawerItemType, selectedUnit);
  }, [rowDrawerOpen, drawerItemType, drawerUnit, loadPolyAssignedUnits, rowDrawerForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAuLabel = (value) => {
    if (value == null || value === "") return "";
    // Loose comparison so a stored numeric id matches an option whose value is the same number
    // regardless of string/number type coming from API / form serialization.
    const stringValue = String(value);
    const option = auFormulaOptions.find(
      (opt) =>
        opt.value === value ||
        opt.label === value ||
        String(opt.value) === stringValue ||
        String(opt.label) === stringValue,
    );
    return option ? String(option.label) : String(value);
  };

  /**
   * Normalize an AU formula label into one of: "cm" | "inch" | "mm" | "lbs" | "kg" | null.
   * Handles variants like "CM", "Centimeter", "Inch", "Inches", "MM", "Millimeter", "LBS", "Pound(s)", "KG", "Kilogram".
   */
  const detectAuUnit = (rawLabel) => {
    const label = String(rawLabel || "").toLowerCase().trim();
    if (!label) return null;
    if (label === "cm" || label.includes("centi")) return "cm";
    if (label.includes("inch")) return "inch";
    if (label === "mm" || label.includes("milli")) return "mm";
    if (label === "lbs" || label === "lb" || label.includes("pound")) return "lbs";
    if (label === "kg" || label === "kgs" || label.includes("kilo")) return "kg";
    if (label === "doz" || label === "dz" || label === "dzn" || label.includes("dozen")) return "doz";
    if (label === "pcs" || label === "pc" || label.includes("piece")) return "pcs";
    return null;
  };

  /**
   * Carton pricing (Rate = per SQM):
   *   CM:   (L+6+W) * (W+4+H) * 2 / 10000 * Rate
   *   INCH: (L+2.3622+W) * (W+1.5748+H) * 2 / 1550 * Rate
   *   MM:   converted to CM (L/10, W/10, H/10) then CM formula
   * Poly pricing (Rate = per LBS / KGS); L/W must be in inches for the formula:
   *   Bag count (PER LBS) = 75000 / L / W / Thickness
   *   Rate/Pcs            = Rate / BagCount
   *   Rate/Dzn (base)     = Rate/Pcs * 12
   *   Rate/Dzn (final)    = Rate/Dzn + AdditionalCharge   (add-on is per dozen)
   *   Total Cost          = PolyTotalQty * Rate/Pcs + (PolyTotalQty / 12) * AdditionalCharge
   * Effective L/W include additional_length / additional_width (flap length/width ÷ 2).
   */
  const getEffectiveDimensions = (rowValues = {}) => {
    const itemTypeLabel = getItemTypeLabel(rowValues.item_type).toLowerCase();
    const isPoly = itemTypeLabel.includes("poly");
    const additionalLength = isPoly ? Number(rowValues.additional_length ?? Number(rowValues.flap_length ?? 0) / 2) : 0;
    const additionalWidth = isPoly ? Number(rowValues.additional_width ?? Number(rowValues.flap_width ?? 0) / 2) : 0;
    return {
      l: Number(rowValues.l ?? 0) + additionalLength,
      w: Number(rowValues.w ?? 0) + additionalWidth,
      h: Number(rowValues.h ?? 0),
    };
  };

  const syncFlapDerivedDimensions = (values = rowDrawerForm.getFieldsValue()) => {
    const flapLength = Number(values.flap_length ?? 0);
    const flapWidth = Number(values.flap_width ?? 0);
    const additionalLength = flapLength / 2;
    const additionalWidth = flapWidth / 2;
    rowDrawerForm.setFieldsValue({
      additional_length: additionalLength,
      additional_width: additionalWidth,
    });
    return {
      ...values,
      additional_length: additionalLength,
      additional_width: additionalWidth,
    };
  };

  const calculateRatePerPcsDzn = (rowValues, sizeValues = {}) => {
    const rate = Number(sizeValues.rate ?? 0);
    const { l: effectiveL, w: effectiveW, h: effectiveH } = getEffectiveDimensions(rowValues);
    let l = effectiveL;
    let w = effectiveW;
    let h = effectiveH;
    const thickness = Number(rowValues.thickness ?? 0);
    const itemTypeLabel = getItemTypeLabel(rowValues.item_type).toLowerCase();
    const auUnit = detectAuUnit(getAuLabel(rowValues.au ?? rowValues.au_formula_id));

    if (!rate || rate <= 0) return 0;

    if (itemTypeLabel.includes("carton")) {
      // MM is the same shape as CM but scaled by /10 per dimension
      if (auUnit === "mm") {
        l /= 10;
        w /= 10;
        h /= 10;
      }
      if (auUnit === "cm" || auUnit === "mm") {
        return (((l + 6 + w) * (w + 4 + h) * 2) / 10000) * rate;
      }
      if (auUnit === "inch") {
        return (((l + 2.3622 + w) * (w + 1.5748 + h) * 2) / 1550) * rate;
      }
      return 0;
    }

    if (itemTypeLabel.includes("poly")) {
      const perPcsPrice = calculatePolyPerPcsRate(rowValues, sizeValues);
      if (!perPcsPrice) return 0;
      if (auUnit === "doz") return perPcsPrice * 12;
      return perPcsPrice;
    }

    return 0;
  };

  /** Poly per-piece rate from L/W/thickness and per-LBS (or per-KG) rate. */
  const calculatePolyPerPcsRate = (rowValues, sizeValues = {}) => {
    const rate = Number(sizeValues.rate ?? 0);
    const { l, w } = getEffectiveDimensions(rowValues);
    const thickness = Number(rowValues.thickness ?? 0);
    if (!rate || !l || !w || !thickness) return 0;
    const divisor = 75000 / l / w / thickness;
    if (!divisor) return 0;
    return rate / divisor;
  };

  /** Rate/Dzn with per-dozen additional: (Rate/Pcs * 12) + AdditionalCharge */
  const calculatePolyRateDznWithAdditional = (ratePcs, additionalChargePerDzn = 0) =>
    roundBreakdownMoney(Number(ratePcs || 0) * 12 + Number(additionalChargePerDzn || 0));

  /** Poly additional total from per-dozen charge: (qty / 12) * AdditionalCharge */
  const calculatePolyAdditionalCost = (polyTotalQty, additionalChargePerDzn = 0) =>
    roundBreakdownMoney((Number(polyTotalQty || 0) / 12) * Number(additionalChargePerDzn || 0));

  const applyPolyRatePair = (sizeIndex, ratePcs, { manual = true } = {}) => {
    const pcs = roundBreakdownMoney(Number(ratePcs || 0));
    const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
    const charge = Number(sizeDetails[sizeIndex]?.additional_charge_per_pcs ?? 0);
    const dzn = calculatePolyRateDznWithAdditional(pcs, charge);
    const next = sizeDetails.map((sd, i) =>
      i === sizeIndex
        ? {
            ...sd,
            rate_pcs: pcs,
            rate_dzn: dzn,
            rate_pcs_dzn: pcs,
            ...(manual ? { manual_poly_rate: true } : { manual_poly_rate: false }),
          }
        : sd,
    );
    rowDrawerForm.setFieldValue("size_details", next);
    return { rate_pcs: pcs, rate_dzn: dzn };
  };

  const handlePolyRatePcsChange = (sizeIndex, value) => {
    const { rate_pcs: pcs } = applyPolyRatePair(sizeIndex, value, { manual: true });
    const rowValues = rowDrawerForm.getFieldsValue();
    const sd = rowValues.size_details?.[sizeIndex] || {};
    const effectiveCartonQty = Number(sd.carton_qty ?? sd.qty_pcs_dzn ?? 0);
    const charge = Number(sd.additional_charge_per_pcs ?? 0);
    // Payload keeps base total_cost; grand_total adds (qty/12)*additional
    const totalCost = roundBreakdownMoney(effectiveCartonQty * pcs);
    rowDrawerForm.setFieldValue(["size_details", sizeIndex, "total_cost"], totalCost);
    syncDrawerSizeGrandTotal(sizeIndex, {
      ...rowValues,
      size_details: (rowValues.size_details || []).map((item, i) =>
        i === sizeIndex
          ? { ...item, rate_pcs: pcs, total_cost: totalCost, additional_charge_per_pcs: charge }
          : item,
      ),
    });
  };

  const handlePolyRateDznChange = (sizeIndex, value) => {
    // Rate/Dzn includes per-dozen additional; reverse: Rate/Pcs = (Rate/Dzn - Additional) / 12
    const dzn = Number(value || 0);
    const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
    const charge = Number(sizeDetails[sizeIndex]?.additional_charge_per_pcs ?? 0);
    const pcs = dzn > 0 ? roundBreakdownMoney((dzn - charge) / 12) : 0;
    handlePolyRatePcsChange(sizeIndex, Math.max(0, pcs));
  };

  /**
   * @param {number} sizeIndex — index in size_details
   * @param {object} rowValues — row-level drawer values (L, W, H, au, item_type, …)
   * @param {object} [syncOverrides] — merge over the size line for this calc only (e.g. pending carton_qty from InputNumber onChange before Form store flushes)
   */
  const syncDrawerSizeCalculations = (sizeIndex, rowValues = rowDrawerForm.getFieldsValue(), syncOverrides = {}) => {
    const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
    const baseDetail = sizeDetails[sizeIndex];
    if (!baseDetail) return;
    const sizeDetail = { ...baseDetail, ...syncOverrides };

    const itemLabel = getItemTypeLabel(rowValues?.item_type).toLowerCase();
    const isPoly = itemLabel.includes("poly");

    const isManualCartonQty = Boolean(sizeDetail.manual_carton_qty);
    const packQty = Number(sizeDetail.pack_qty || 1);
    const garmentQty = Number(sizeDetail.garment_qty || 0);
    const currentCartonQty = Number(sizeDetail.carton_qty || 0);
    const qtyPcsDzn = Number(sizeDetail.qty_pcs_dzn || 0);
    const cartonFactor = Number(rowValues.carton_formula_factor) || 1.03;
    const cartonAdd = Number(rowValues.carton_formula_add) || 2;

    // For poly rows the per-line total qty (used as multiplier in Total Cost) is
    // the size's Qty(Pcs/Dzn). For carton rows it's the carton-pack formula.
    const formulaCartonQty = isPoly
      ? qtyPcsDzn
      : garmentQty > 0 && packQty > 0
        ? Math.round((garmentQty / packQty) * cartonFactor) + cartonAdd
        : currentCartonQty;
    const effectiveCartonQty = isPoly
      ? isManualCartonQty
        ? currentCartonQty
        : qtyPcsDzn
      : isManualCartonQty
        ? currentCartonQty
        : formulaCartonQty;
    const calculatedRatePcsDzn = calculateRatePerPcsDzn(rowValues, { ...sizeDetail, carton_qty: effectiveCartonQty });

    let ratePcsForTotal = calculatedRatePcsDzn;
    if (isPoly) {
      const charge = Number(sizeDetail.additional_charge_per_pcs ?? 0);
      if (sizeDetail.manual_poly_rate) {
        ratePcsForTotal = Number(sizeDetail.rate_pcs ?? 0);
      } else {
        const perPcs = roundBreakdownMoney(calculatePolyPerPcsRate(rowValues, sizeDetail));
        if (Number(baseDetail.rate_pcs || 0) !== perPcs) {
          rowDrawerForm.setFieldValue(["size_details", sizeIndex, "rate_pcs"], perPcs);
        }
        ratePcsForTotal = perPcs;
      }
      const perDzn = calculatePolyRateDznWithAdditional(ratePcsForTotal, charge);
      if (Number(baseDetail.rate_dzn || 0) !== perDzn) {
        rowDrawerForm.setFieldValue(["size_details", sizeIndex, "rate_dzn"], perDzn);
      }
    }

    const calculatedTotalCost = Number(effectiveCartonQty) * Number(ratePcsForTotal || 0);

    if (isPoly) {
      if (Number(sizeDetail.qty_pcs_dzn || 0) !== Number(effectiveCartonQty)) {
        rowDrawerForm.setFieldValue(["size_details", sizeIndex, "qty_pcs_dzn"], effectiveCartonQty);
      }
      if (Number(sizeDetail.carton_qty || 0) !== Number(effectiveCartonQty)) {
        rowDrawerForm.setFieldValue(["size_details", sizeIndex, "carton_qty"], effectiveCartonQty);
      }
    } else if (!isManualCartonQty && Number(sizeDetail.carton_qty || 0) !== Number(formulaCartonQty)) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "carton_qty"], formulaCartonQty);
    }

    const nextRatePcsDzn = roundBreakdownMoney(isPoly ? ratePcsForTotal : calculatedRatePcsDzn);
    if (Number(baseDetail.rate_pcs_dzn || 0) !== nextRatePcsDzn) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "rate_pcs_dzn"], nextRatePcsDzn);
    }

    const nextTotalCost = roundBreakdownMoney(calculatedTotalCost);
    if (Number(baseDetail.total_cost || 0) !== nextTotalCost) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "total_cost"], nextTotalCost);
    }

    syncDrawerSizeGrandTotal(sizeIndex, {
      ...rowValues,
      size_details: (rowValues.size_details || []).map((sd, i) =>
        i === sizeIndex ? { ...sd, total_cost: nextTotalCost } : sd,
      ),
    });

    if (isCartonBoxItemType(rowValues?.item_type, getItemTypeLabel)) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "qty_pcs_dzn"], null);
    }
  };

  const syncDrawerSizeGrandTotal = (sizeIndex, rowValues = rowDrawerForm.getFieldsValue()) => {
    const sd = rowValues.size_details?.[sizeIndex] || rowDrawerForm.getFieldValue(["size_details", sizeIndex]) || {};
    const lineTotal = Number(sd.total_cost ?? 0);
    const cartonQty = Number(sd.carton_qty ?? 0);
    const charge = Number(sd.additional_charge_per_pcs ?? 0);
    const isPoly = getItemTypeLabel(rowValues?.item_type).toLowerCase().includes("poly");
    // Poly Additional Charge is per dozen; carton remains per pcs
    const additionalCost = isPoly
      ? calculatePolyAdditionalCost(cartonQty, charge)
      : roundBreakdownMoney(cartonQty * charge);
    if (Number(sd.additional_cost ?? 0) !== additionalCost) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "additional_cost"], additionalCost);
    }
    const grandTotal = roundBreakdownMoney(lineTotal + additionalCost);
    if (Number(sd.grand_total ?? 0) !== grandTotal) {
      rowDrawerForm.setFieldValue(["size_details", sizeIndex, "grand_total"], grandTotal);
    }
  };

  const syncAllDrawerSizeCalculations = (rowValues = rowDrawerForm.getFieldsValue()) => {
    const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
    sizeDetails.forEach((_, index) => syncDrawerSizeCalculations(index, rowValues));
    const latestValues = rowDrawerForm.getFieldsValue();
    (latestValues.size_details || []).forEach((_, index) => syncDrawerSizeGrandTotal(index, latestValues));
    const itemLabel = getItemTypeLabel(latestValues?.item_type).toLowerCase();
    if (itemLabel.includes("poly")) {
      const sumQty = (latestValues.size_details || []).reduce((s, sd) => s + Number(sd?.qty_pcs_dzn ?? 0), 0);
      rowDrawerForm.setFieldValue("poly_total_qty", sumQty);
    }
  };

  const queueDrawerRecalculation = (clearPolyManual = false) => {
    setTimeout(() => {
      if (clearPolyManual) {
        const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
        rowDrawerForm.setFieldValue(
          "size_details",
          sizeDetails.map((sd) => ({ ...sd, manual_poly_rate: false })),
        );
      }
      syncAllDrawerSizeCalculations(rowDrawerForm.getFieldsValue());
    }, 0);
  };

  const queueDrawerRecalculationForSize = (sizeIndex) => {
    setTimeout(() => {
      const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
      rowDrawerForm.setFieldValue(
        "size_details",
        sizeDetails.map((sd, i) => (i === sizeIndex ? { ...sd, manual_poly_rate: false } : sd)),
      );
      syncAllDrawerSizeCalculations(rowDrawerForm.getFieldsValue());
    }, 0);
  };

  /**
   * A/U options allowed per item type:
   *   Carton  -> CM / INCH / MM
   *   Poly    -> LBS / KGS
   * Other / unknown item type -> show full list.
   */
  const getAllowedAuOptions = (itemType) => {
    const label = getItemTypeLabel(itemType).toLowerCase();
    if (label.includes("carton")) {
      return auFormulaOptions.filter((opt) => {
        const u = detectAuUnit(opt?.label);
        return u === "cm" || u === "inch" || u === "mm";
      });
    }
    if (label.includes("poly")) {
      return auFormulaOptions.filter((opt) => {
        const u = detectAuUnit(opt?.label);
        return u === "lbs" || u === "kg";
      });
    }
    return auFormulaOptions;
  };

  /**
   * Returns true if the currently selected A/U is allowed for the given item type.
   * Used to clear A/U when item type changes to an incompatible one.
   */
  const isAuValueAllowedForItemType = (auValue, itemType) => {
    if (auValue == null || auValue === "") return true;
    const allowed = getAllowedAuOptions(itemType);
    const sv = String(auValue);
    return allowed.some(
      (opt) => opt.value === auValue || String(opt.value) === sv || String(opt.label) === sv,
    );
  };

  const handleDrawerItemTypeChange = (nextItemType) => {
    const current = rowDrawerForm.getFieldsValue();
    if (current.item_type === nextItemType) return;

    let merged = applyItemTypeFieldResets(
      { ...current, item_type: nextItemType },
      nextItemType,
      getItemTypeLabel,
    );

    if (!isAuValueAllowedForItemType(merged.au, nextItemType)) {
      merged.au = undefined;
      merged.au_formula_id = undefined;
    }

    if (isCartonBoxItemType(nextItemType, getItemTypeLabel)) {
      merged = stripCartonBoxExcludedFields(merged, getItemTypeLabel);
    } else if (isPolyItemType(nextItemType, getItemTypeLabel)) {
      merged = stripPolyExcludedFields(merged, getItemTypeLabel);
    } else {
      setPolyUnitOptions([]);
    }

    rowDrawerForm.setFieldsValue(merged);
    queueDrawerRecalculation();
  };

  const getSupportingMaterialTypeLabel = (value) => {
    if (value == null || value === "") return "";
    const normalized = String(value).toLowerCase();
    const option = supportingMaterialTypeOptions.find(
      (opt) => String(opt?.value) === String(value) || String(opt?.label || "").toLowerCase() === normalized,
    );
    return option ? String(option.label) : String(value);
  };

  const getSupportingMaterialAuLabel = (value) => {
    if (value == null || value === "") return "";
    const normalized = String(value).toLowerCase();
    const option = auFormulaOptions.find(
      (opt) => String(opt?.value) === String(value) || String(opt?.label || "").toLowerCase() === normalized,
    );
    return option ? String(option.label) : String(value);
  };

  /**
   * Classify a supporting-material type label into "top_bottom" (uses L*W)
   * or "divider" (uses L*H — covers X Divider, U Divider, Divider, Box Divider).
   */
  const detectSupportingMaterialShape = (rawLabel) => {
    const label = String(rawLabel || "").toLowerCase();
    if (!label) return null;
    if (label.includes("divider")) return "divider";
    if (label.includes("top") || label.includes("bottom")) return "top_bottom";
    return null;
  };

  /**
   * Supporting material pricing:
   *   Top/Bottom (CM / MM)   = (L*W) / 10000 * Rate  (MM: L/W/H ÷10 then same as CM)
   *   Top/Bottom (INCH) = (L*W) / 1550  * Rate
   *   X/U/Box Divider (CM / MM)   = (L*H) / 10000 * Rate
   *   X/U/Box Divider (INCH) = (L*H) / 1550  * Rate
   */
  const calculateSupportingRatePerQty = (values = {}) => {
    const typeLabel = getSupportingMaterialTypeLabel(values.material_type);
    const auLabel = getSupportingMaterialAuLabel(values.material_au);
    let l = Number(values.material_l ?? 0);
    let w = Number(values.material_w ?? 0);
    let h = Number(values.material_h ?? 0);
    const rate = Number(values.material_rate ?? 0);

    if (!rate || !l) return 0;

    const auUnit = detectAuUnit(auLabel);
    if (auUnit === "mm") {
      l /= 10;
      w /= 10;
      h /= 10;
    }
    const divisor = auUnit === "cm" || auUnit === "mm" ? 10000 : auUnit === "inch" ? 1550 : 0;
    if (!divisor) return 0;

    const shape = detectSupportingMaterialShape(typeLabel);
    if (shape === "top_bottom") {
      if (!w) return 0;
      return ((l * w) / divisor) * rate;
    }
    if (shape === "divider") {
      if (!h) return 0;
      return ((l * h) / divisor) * rate;
    }

    return 0;
  };

  const syncSupportingMaterialCalculations = (allValues = materialForm.getFieldsValue()) => {
    const ratePerQty = calculateSupportingRatePerQty(allValues);
    const qty = Number(allValues.material_qty ?? 0);
    const total = Number(ratePerQty) * qty;

    const nextRatePerQty = roundBreakdownMoney(ratePerQty);
    const nextTotal = roundBreakdownMoney(total);
    const currentRatePerQty = Number(allValues.material_rate_pcs_doz ?? 0);
    const currentTotal = Number(allValues.material_total_cost ?? 0);

    const updates = {};
    if (currentRatePerQty !== nextRatePerQty) updates.material_rate_pcs_doz = nextRatePerQty;
    if (currentTotal !== nextTotal) updates.material_total_cost = nextTotal;

    if (Object.keys(updates).length > 0) {
      materialForm.setFieldsValue(updates);
    }
  };

  // Initialize selectedStyles from form values
  useEffect(() => {
    const breakdownDetails = form.getFieldValue("breakdown_details") || [];
    const stylesMap = {};
    breakdownDetails.forEach((row, index) => {
      if (row.style) {
        stylesMap[index] = row.style;
      }
    });
    if (Object.keys(stylesMap).length > 0) {
      setSelectedStyles((prev) => ({ ...prev, ...stylesMap }));
    }
  }, [form, buyerId]);

  const fetchGarmentQtyForDrawer = async (overrides = {}) => {
    const values = { ...rowDrawerForm.getFieldsValue(), ...overrides };
    const salesContractId = values.sales_contract_id;
    if (!salesContractId) return;
    if (getItemTypeLabel(values.item_type).toLowerCase().includes("poly")) return;
    const styleId = values.style ?? values.style_id ?? null;
    const poMapId = values.po ?? values.po_map_id ?? null;
    const subStyleId = values.sub_style_id ?? null;
    try {
      const response = await postData(BOOKING_CARTON_GET_GARMENT_QTY, {
        sales_contract_id: salesContractId,
        style_id: styleId,
        po_map_id: poMapId,
        sub_style_id: subStyleId,
      });
      const qty = response?.data?.data ?? response?.data ?? 0;
      const sizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
      const nextSizeDetails = sizeDetails.map((sd) => ({ ...sd, garment_qty: qty }));
      rowDrawerForm.setFieldsValue({ garment_qty: qty, size_details: nextSizeDetails });
      queueDrawerRecalculation();
    } catch (error) {
      console.error("Error fetching garment qty:", error);
    }
  };

  const handleDrawerSizeSelection = (selectedSizeIds) => {
    setDrawerSelectedSizes(selectedSizeIds);
    const existingSizeDetails = rowDrawerForm.getFieldValue("size_details") || [];
    const usedIndices = new Set();
    const sizeIds = selectedSizeIds.length ? selectedSizeIds : [null];

    const resolveExistingForSize = (sizeId, previousRow) => {
      const exactIdx = existingSizeDetails.findIndex(
        (sd, i) => !usedIndices.has(i) && sizesMatch(sd.size_id, sizeId),
      );
      if (exactIdx !== -1) {
        usedIndices.add(exactIdx);
        const existing = existingSizeDetails[exactIdx];
        return {
          ...existing,
          size_id: sizeId,
          size_name: getSizeLabelFromOptions(sizeId, availableSizes) || existing.size_name,
        };
      }

      const orphanIdx = existingSizeDetails.findIndex(
        (sd, i) => !usedIndices.has(i) && sd.size_id == null,
      );
      if (orphanIdx !== -1) {
        usedIndices.add(orphanIdx);
        const orphan = existingSizeDetails[orphanIdx];
        return {
          ...orphan,
          size_id: sizeId,
          size_name: getSizeLabelFromOptions(sizeId, availableSizes) || orphan.size_name,
        };
      }

      return copySizeDetailFromPrevious(previousRow, sizeId, availableSizes);
    };

    let previousRow = existingSizeDetails.length ? existingSizeDetails[existingSizeDetails.length - 1] : null;
    const nextSizeDetails = sizeIds.map((sizeId) => {
      const row = resolveExistingForSize(sizeId, previousRow);
      previousRow = row;
      return row;
    });

    rowDrawerForm.setFieldsValue({ size_details: nextSizeDetails });
    setDrawerSizeIds(sizeIds);
    setTimeout(() => {
      syncAllDrawerSizeCalculations({
        ...rowDrawerForm.getFieldsValue(),
        size_details: nextSizeDetails,
      });
    }, 0);
  };

  const updateCartonTotalQty = (rowIndex, sizeIndex) => {
    const garmentQty = form.getFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "garment_qty"]);
    const packQty = form.getFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "pack_qty"]) || 1;
    const factor = form.getFieldValue(["breakdown_details", rowIndex, "carton_formula_factor"]) || 1.03;
    const add = form.getFieldValue(["breakdown_details", rowIndex, "carton_formula_add"]) || 2;

    if (garmentQty != null && Number(packQty) > 0) {
      const val = Math.round((Number(garmentQty) / Number(packQty)) * factor) + add;
      form.setFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "carton_qty"], val);
      updateSizeTotalCost(rowIndex, sizeIndex);
    }
  };

  const updateSizeTotalCost = (rowIndex, sizeIndex) => {
    const cartonQty = form.getFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "carton_qty"]);
    const ratePcsDzn = form.getFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "rate_pcs_dzn"]);
    if (cartonQty != null && ratePcsDzn != null) {
      const totalCost = roundBreakdownMoney(Number(cartonQty) * Number(ratePcsDzn));
      form.setFieldValue(["breakdown_details", rowIndex, "size_details", sizeIndex, "total_cost"], totalCost);
    }
  };

  const updateRowTotalCost = (rowIndex) => {
    const row = form.getFieldValue(["breakdown_details", rowIndex]) || {};
    const sizeDetails = row.size_details || [];
    const subtotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.total_cost) || 0), 0);
    const additional = sizeDetails.reduce((sum, sd) => sum + (Number(sd.additional_cost) || 0), 0);
    const grandTotal = sizeDetails.reduce((sum, sd) => sum + (Number(sd.grand_total) || 0), 0);
    form.setFieldValue(["breakdown_details", rowIndex, "total_cost"], roundBreakdownMoney(subtotal));
    form.setFieldValue(["breakdown_details", rowIndex, "additional_cost"], roundBreakdownMoney(additional));
    form.setFieldValue(["breakdown_details", rowIndex, "grand_total"], roundBreakdownMoney(grandTotal));
  };

  const openRowDrawer = (rowIndex, { resetContractFields = false } = {}) => {
    const breakdownDetails = getBreakdownRowsSnapshot();
    seedLookupListsFromBreakdownRows(breakdownDetails);
    const rowData = breakdownDetails[rowIndex] || {};
    setRowDrawerIndex(rowIndex);
    rowDrawerIndexRef.current = rowIndex;

    if (resetContractFields) {
      setDrawerSalesContractData(null);
      setDrawerPoOptions([]);
      setDrawerStyleOptions([]);
      setSubStyleSearchOptions([]);
    }

    const existingSizeDetails = rowData.size_details || [];
    let sizesToUse = existingSizeDetails.length > 0 ? existingSizeDetails.map((sd) => sd.size_id) : drawerSelectedSizes;

    if (!sizesToUse.length && !existingSizeDetails.length) {
      sizesToUse = [null];
    }

    setDrawerSizeIds(sizesToUse);
    setDrawerSelectedSizes(sizesToUse.filter((id) => id != null));

    if (!resetContractFields && rowData.sales_contract_id) {
      seedDrawerSelectOptionsFromRow(rowData);
      fetchDrawerSalesContractData(rowData.sales_contract_id).then(() => {
        const latestRow = getBreakdownRowsSnapshot()[rowIndex] || rowData;
        seedDrawerSelectOptionsFromRow(latestRow);
        const drawerValues = rowDrawerForm.getFieldsValue();
        if (drawerValues.style) {
          applyDrawerStyleSelection(drawerValues.style);
        }
      });
    } else if (!resetContractFields) {
      seedDrawerSelectOptionsFromRow(rowData);
    }

    const initializedSizeDetails = [];
    sizesToUse.forEach((sizeId) => {
      const existing = existingSizeDetails.find((sd) => sizesMatch(sd.size_id, sizeId));
      if (existing) {
        const pcs = Number(existing.rate_pcs ?? existing.rate_pcs_dzn ?? 0);
        initializedSizeDetails.push({
          ...existing,
          rate_pcs: existing.rate_pcs ?? pcs,
          rate_dzn: existing.rate_dzn ?? roundBreakdownMoney(pcs * 12),
        });
        return;
      }
      const previousRow =
        initializedSizeDetails[initializedSizeDetails.length - 1] ??
        existingSizeDetails[existingSizeDetails.length - 1] ??
        null;
      initializedSizeDetails.push({
        ...copySizeDetailFromPrevious(previousRow, sizeId, availableSizes),
        flap_length: 0,
        gusset_size: 0,
        thickness: 0,
        hanger_gap_size: 0,
      });
    });

    const poFromRow = !resetContractFields && rowData.po ? getSelectedPOInfo(rowData.po) : null;
    let shipmentVal = resetContractFields ? undefined : rowData.shipment_date;
    if (!resetContractFields && shipmentVal != null && shipmentVal !== "" && !moment.isMoment(shipmentVal)) {
      const parsed = moment(shipmentVal);
      shipmentVal = parsed.isValid() ? parsed : undefined;
    }
    if (!resetContractFields && !shipmentVal && poFromRow?.delivery_date) {
      const m = moment(poFromRow.delivery_date);
      shipmentVal = m.isValid() ? m : undefined;
    }

    const sharedDrawerValues = {
      poly_total_qty: rowData.poly_total_qty ?? 0,
      additional_length: rowData.additional_length ?? 0,
      additional_width: rowData.additional_width ?? 0,
      flap_width:
        rowData.flap_width ??
        (rowData.additional_width != null ? Number(rowData.additional_width) * 2 : 0),
      flap_length:
        rowData.flap_length ??
        (rowData.additional_length != null ? Number(rowData.additional_length) * 2 : 0),
      l: rowData.l ?? 0,
      w: rowData.w ?? 0,
      h: rowData.h ?? 0,
      au: rowData.au ?? rowData.au_formula_id,
      au_formula_id: rowData.au_formula_id ?? rowData.au,
      unit: rowData.unit_id ?? rowData.unit,
      size_details: initializedSizeDetails,
    };

    if (resetContractFields) {
      rowDrawerForm.resetFields();
      rowDrawerForm.setFieldsValue({
        ...rowData,
        ...COPY_RESET_CONTRACT_FIELDS,
        ...sharedDrawerValues,
      });
    } else {
      rowDrawerForm.setFieldsValue({
        ...rowData,
        sales_contract_id: rowData.sales_contract_id,
        style: rowData.style ?? rowData.style_id,
        style_id: rowData.style_id ?? rowData.style,
        sub_style_id: rowData.sub_style_id ?? null,
        brand: rowData.brand ?? undefined,
        style_description: rowData.style_description ?? undefined,
        shipment_date: shipmentVal,
        ...sharedDrawerValues,
      });
    }

    syncAllDrawerSizeCalculations({
      ...rowData,
      ...(resetContractFields ? COPY_RESET_CONTRACT_FIELDS : {}),
      au: rowData.au ?? rowData.au_formula_id,
      au_formula_id: rowData.au_formula_id ?? rowData.au,
      shipment_date: resetContractFields ? undefined : shipmentVal,
      size_details: initializedSizeDetails,
    });
    setRowDrawerOpen(true);
  };

  const applyDrawerStyleSelection = (styleId, option) => {
    if (styleId == null) {
      rowDrawerForm.setFieldsValue({
        style: undefined,
        style_id: undefined,
        brand: undefined,
        style_description: undefined,
      });
      return;
    }
    const matched =
      (option && typeof option === "object" && option.value === styleId ? option : null) ||
      drawerStyleOptions.find((opt) => opt.value === styleId) ||
      getSelectedStyleInfo(styleId);
    const brand = matched?.brand ?? matched?.style_info?.brand_info?.name ?? undefined;
    const styleDescription = matched?.description ?? matched?.style_info?.description ?? undefined;
    rowDrawerForm.setFieldsValue({
      style: styleId,
      style_id: styleId,
      brand,
      style_description: styleDescription,
    });
  };

  const persistDrawerDraftToRow = () => {
    const targetIndex = rowDrawerIndexRef.current;
    if (targetIndex == null) return;
    syncAllDrawerSizeCalculations(rowDrawerForm.getFieldsValue());
    const latestValues = rowDrawerForm.getFieldsValue();
    const sizeDetails = latestValues.size_details || [];
    const seen = new Set();
    const uniqueSizeDetails = sizeDetails.filter((sd) => {
      const id = sd?.size_id;
      const dedupeKey = id == null ? "__null__" : id;
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
    const normalizedValues = normalizeDrawerRowValues(
      { ...latestValues, size_details: uniqueSizeDetails },
      getItemTypeLabel,
    );
    const breakdownDetails = getBreakdownRowsSnapshot();
    const existingRow = breakdownDetails[targetIndex] || {};
    const updatedRow = enrichRowWithLookupRefs(
      {
        ...existingRow,
        ...normalizedValues,
        id: existingRow.id ?? 0,
        booking_carton_break_down_id: existingRow.booking_carton_break_down_id ?? 0,
        size_details: normalizedValues.size_details ?? existingRow.size_details ?? [],
      },
      existingRow,
    );
    const nextRows = breakdownDetails.map((row, idx) => (idx === targetIndex ? updatedRow : row));
    form.setFieldsValue({ breakdown_details: nextRows });
    seedLookupListsFromBreakdownRows(nextRows);
    bumpBreakdownTable();
  };

  const closeRowDrawer = ({ discard = false } = {}) => {
    if (!discard) {
      persistDrawerDraftToRow();
    }
    setRowDrawerOpen(false);
    setRowDrawerIndex(null);
    rowDrawerIndexRef.current = null;
    setDrawerSizeIds([]);
  };

  const handleCopyBreakdownRow = (rowIndex) => {
    const breakdownDetails = getBreakdownRowsSnapshot();
    const sourceRow = breakdownDetails[rowIndex];
    if (!sourceRow) return;
    const copiedRow = cloneBreakdownRowForCopy(sourceRow);
    const insertIndex = rowIndex + 1;
    const nextRows = [...breakdownDetails];
    nextRows.splice(insertIndex, 0, copiedRow);
    form.setFieldsValue({ breakdown_details: nextRows });
    seedLookupListsFromBreakdownRows(nextRows);
    bumpBreakdownTable();
    setTimeout(() => openRowDrawer(insertIndex, { resetContractFields: true }), 0);
  };

  const getGarmentQtyForTargetKey = (rowKey) => {
    if (!rowKey) return 0;
    const { rowIndex, sizeId } = parseBreakdownRowSizeKey(rowKey);
    const sizeDetails = form.getFieldValue(["breakdown_details", rowIndex, "size_details"]) || [];
    const sizeDetail = sizeDetails.find((sd) => sizesMatch(sd.size_id, sizeId));
    return Number(sizeDetail?.garment_qty) || 0;
  };

  /** Material qty = garment qty of the primary linked size row (not a sum across rows). */
  const resolveMaterialQtyFromGarment = (keys, materialType) => {
    const targetKeys = Array.isArray(keys) ? keys.filter(Boolean) : [];
    const primaryKey = targetKeys.length ? targetKeys[targetKeys.length - 1] : null;
    const base = getGarmentQtyForTargetKey(primaryKey);
    const typeLabel = getSupportingMaterialTypeLabel(materialType);
    const multiplier = detectSupportingMaterialShape(typeLabel) === "top_bottom" ? 2 : 1;
    return base * multiplier;
  };

  const syncMaterialQtyFromTargets = (materialType) => {
    const keys = materialForm.getFieldValue("material_target_keys") || selectedRowKeys;
    const qty = resolveMaterialQtyFromGarment(keys, materialType);
    materialForm.setFieldsValue({ material_qty: qty });
    syncSupportingMaterialCalculations({ ...materialForm.getFieldsValue(), material_qty: qty });
  };

  const openBulkMaterialModal = () => {
    if (!selectedRowKeys.length) {
      alertPop("error", "Select at least one carton line in the breakdown table.");
      return;
    }
    const keys = dedupeTargetKeys(selectedRowKeys);
    const materialType = materialForm.getFieldValue("material_type");
    const materialQty = resolveMaterialQtyFromGarment(keys, materialType);
    materialForm.resetFields();
    materialForm.setFieldsValue({
      material_target_keys: keys,
      material_qty: materialQty,
      material_l: 0,
      material_h: 0,
      material_w: 0,
      material_rate: 0,
      material_rate_pcs_doz: 0,
      material_total_cost: 0,
    });
    setMaterialModalOpen(true);
  };

  const closeMaterialModal = () => {
    setMaterialModalOpen(false);
    materialForm.resetFields();
    materialForm.setFieldsValue({
      material_qty: 0,
      material_l: 0,
      material_h: 0,
      material_w: 0,
      material_rate: 0,
      material_rate_pcs_doz: 0,
      material_total_cost: 0,
      material_target_keys: [],
    });
  };

  const handleMaterialSave = () => {
    materialForm
      .validateFields()
      .then(async (values) => {
        const rawTargetKeys = dedupeTargetKeys(
          Array.isArray(values.material_target_keys) && values.material_target_keys.length > 0
            ? values.material_target_keys
            : selectedRowKeys,
        );

        // Supporting material applies to carton rows only; silently drop any poly rows
        // that may have leaked into the selection.
        const breakdownDetailsForFilter = form.getFieldValue("breakdown_details") || [];
        const targetKeys = rawTargetKeys.filter((key) => {
          const { rowIndex } = parseBreakdownRowSizeKey(key);
          const row = breakdownDetailsForFilter[rowIndex];
          return row && !rowIsPolyBag(row);
        });

        if (bookingCartonId && onSaveSupportingMaterial) {
          if (!editingMaterial && !targetKeys.length) {
            alertPop("error", "Select carton lines in the breakdown table or pick targets in Link to sizes / rows.");
            return;
          }
          let breakdowns = [];

          const breakdownDetails = form.getFieldValue("breakdown_details") || [];
          const missingLineIds = targetKeys.filter(
            (key) => resolveBreakdownLineIdForTargetKey(breakdownDetails, key) == null,
          );
          if (missingLineIds.length) {
            alertPop("error", "Save carton breakdown lines first before linking supporting material to sizes.");
            return;
          }
          breakdowns = buildSupportingMaterialBreakdownPayload({
            targetKeys,
            breakdownDetails,
            editingMaterial,
            editingRelations,
          });
          const payload = {
            supporting_materials: [
              {
                id: editingMaterial?.id || 0,
                material_type_id: Number(values.material_type) || values.material_type,
                ply_id: Number(values.material_ply) || values.material_ply,
                a_u_formula_id: Number(values.material_au) || values.material_au,
                material_qty: values.material_qty,
                length: values.material_l,
                width: values.material_w,
                height: values.material_h,
                rate: values.material_rate,
                rate_per_qty: values.material_rate_pcs_doz ?? 1,
                total_amount: values.material_total_cost,
                is_delete: 0,
                breakdowns: breakdowns.length ? breakdowns : [{ id: 0, booking_carton_break_down_id: 0, is_delete: 0 }],
              },
            ],
          };
          const response = await onSaveSupportingMaterial(bookingCartonId, payload);
          if (response?.code === 200) {
            const updated = response?.data?.data?.supporting_materials ?? [];
            if (Array.isArray(updated)) {
              setApiSupportingMaterials(updated);
            }
            setSelectedRowKeys([]);
            setEditingMaterial(null);
            setEditingRelations([]);
            closeMaterialModal();
          }
        } else {
          setMaterialDetailsList((prev) => [
            ...prev,
            { ...values, id: Date.now(), linked_rows: [...targetKeys] },
          ]);
          setSelectedRowKeys([]);
          closeMaterialModal();
        }
      })
      .catch(() => {
        // keep modal open on validation error
      });
  };

  const handleDeleteMaterial = (materialId) => {
    setMaterialDetailsList((prev) => prev.filter((m) => m.id !== materialId));
  };

  const handleEditSupportingMaterial = (mat) => {
    setEditingMaterial(mat);
    const rels = mat.supporting_detail_relations || mat.breakdowns || [];
    setEditingRelations(rels);

    const breakdownDetails = form.getFieldValue("breakdown_details") || [];
    const material_target_keys = dedupeTargetKeys(
      (rels || [])
        .map((rel) => findTargetKeyForBreakdownRelation(breakdownDetails, rel, rowIsPolyBag))
        .filter(Boolean),
    );

    materialForm.resetFields();
    materialForm.setFieldsValue({
      material_type: mat.material_type_id,
      material_ply: mat.ply_id,
      material_au: mat.a_u_formula_id,
      material_qty: Number(mat.material_qty) || mat.material_qty,
      material_l: Number(mat.length) || mat.length,
      material_w: Number(mat.width) || mat.width,
      material_h: Number(mat.height) || mat.height,
      material_rate: Number(mat.rate) || mat.rate,
      material_rate_pcs_doz: Number(mat.rate_per_qty) || mat.rate_per_qty,
      material_total_cost: Number(mat.total_amount) || mat.total_amount,
      material_target_keys,
    });
    setMaterialModalOpen(true);
  };

  const handleCopySupportingMaterial = (mat) => {
    setEditingMaterial(null);
    setEditingRelations([]);
    materialForm.resetFields();
    materialForm.setFieldsValue({
      material_type: mat.material_type_id,
      material_ply: mat.ply_id,
      material_au: mat.a_u_formula_id,
      material_qty: Number(mat.material_qty) || mat.material_qty,
      material_l: Number(mat.length) || mat.length,
      material_w: Number(mat.width) || mat.width,
      material_h: Number(mat.height) || mat.height,
      material_rate: Number(mat.rate) || mat.rate,
      material_rate_pcs_doz: Number(mat.rate_per_qty) || mat.rate_per_qty,
      material_total_cost: Number(mat.total_amount) || mat.total_amount,
      material_target_keys: [],
    });
    setMaterialModalOpen(true);
  };

  const handleApiDeleteSupportingMaterial = async (mat) => {
    if (!bookingCartonId || !onSaveSupportingMaterial) return;
    const rels = mat.supporting_detail_relations || mat.breakdowns || [];
    const breakdowns = (rels || []).map((rel) => ({
      id: rel.id || 0,
      booking_carton_break_down_id: rel.booking_carton_break_down_id,
      ...(rel.size_id && { size_id: rel.size_id }),
      is_delete: 1,
    }));

    const payload = {
      supporting_materials: [
        {
          id: mat.id,
          material_type_id: mat.material_type_id,
          ply_id: mat.ply_id,
          a_u_formula_id: mat.a_u_formula_id,
          material_qty: mat.material_qty,
          length: mat.length,
          width: mat.width,
          height: mat.height,
          rate: mat.rate,
          rate_per_qty: mat.rate_per_qty,
          total_amount: mat.total_amount,
          is_delete: 1,
          breakdowns,
        },
      ],
    };

    const response = await onSaveSupportingMaterial(bookingCartonId, payload);
    if (response?.code === 200) {
      const updated = response?.data?.data?.supporting_materials ?? [];
      if (Array.isArray(updated)) setApiSupportingMaterials(updated);
    }
  };

  // Row selection helpers — poly rows cannot have supporting material, so they
  // are excluded from selection.
  const getVisibleRowIndices = () => collectCartonBreakdownTargetKeys(form.getFieldValue("breakdown_details") || [], rowIsPolyBag);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRowKeys(getVisibleRowIndices());
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (rowKey, checked) => {
    setSelectedRowKeys((prev) =>
      checked ? (prev.includes(rowKey) ? prev : [...prev, rowKey]) : prev.filter((k) => k !== rowKey),
    );
  };

  const handleRowDrawerSubmit = () => {
    if (isSubmittingDrawerRef.current) return;
    isSubmittingDrawerRef.current = true;

    rowDrawerForm
      .validateFields()
      .then((values) => {
        syncAllDrawerSizeCalculations(values);
        const latestValues = rowDrawerForm.getFieldsValue();
        // Deduplicate size_details by size_id so one row per size (avoids extra table rows)
        const sizeDetails = latestValues.size_details || [];
        const seen = new Set();
        const uniqueSizeDetails = sizeDetails.filter((sd) => {
          const id = sd?.size_id;
          const dedupeKey = id == null ? "__null__" : id;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          return true;
        });
        const normalizedValues = normalizeDrawerRowValues(
          { ...latestValues, size_details: uniqueSizeDetails },
          getItemTypeLabel,
        );

        const syncParentBreakdownRows = (nextRows) => {
          form.setFieldsValue({ breakdown_details: nextRows });
          seedLookupListsFromBreakdownRows(nextRows);
          bumpBreakdownTable();
        };

        const targetIndex = rowDrawerIndexRef.current ?? rowDrawerIndex;
        if (targetIndex != null) {
          const breakdownDetails = getBreakdownRowsSnapshot();
          const existingRow = breakdownDetails[targetIndex] || {};
          const updatedRow = enrichRowWithLookupRefs(
            {
              ...existingRow,
              ...normalizedValues,
              id: existingRow.id ?? 0,
              booking_carton_break_down_id: existingRow.booking_carton_break_down_id ?? 0,
              size_details: normalizedValues.size_details ?? existingRow.size_details ?? [],
            },
            existingRow,
          );
          const nextRows = breakdownDetails.map((row, idx) => (idx === targetIndex ? updatedRow : row));
          syncParentBreakdownRows(nextRows);
          updateRowTotalCost(targetIndex);
        } else {
          const breakdownDetails = getBreakdownRowsSnapshot();
          syncParentBreakdownRows([
            ...breakdownDetails,
            enrichRowWithLookupRefs(normalizedValues, {}),
          ]);
        }
        closeRowDrawer({ discard: true });
      })
      .catch(() => {
        // Validation failed; keep drawer open
      })
      .finally(() => {
        isSubmittingDrawerRef.current = false;
      });
  };

  const getSelectedStyleInfo = (styleId) => {
    const fromDrawer = drawerStyleOptions.find((opt) => opt.value === styleId);
    if (fromDrawer) {
      return {
        style_id: styleId,
        style_info: {
          style_no: fromDrawer.label,
          brand_info: fromDrawer.brand ? { name: fromDrawer.brand } : undefined,
          description: fromDrawer.description,
        },
      };
    }
    return styleList.find((s) => s.style_id === styleId);
  };

  const getSelectedPOInfo = (poId) => {
    return drawerPoOptions.find((p) => p.id === poId) || poList.find((p) => p.id === poId);
  };

  const enrichRowWithLookupRefs = (values, existingRow = {}) => {
    const next = { ...values };
    const poId = next.po ?? next.po_map_id ?? existingRow.po ?? existingRow.po_map_id;
    if (poId != null) {
      const poFromList = poList.find((p) => p.id === poId);
      const poFromDrawer = drawerPoOptions.find((p) => p.id === poId);
      const po =
        poFromList ||
        poFromDrawer ||
        (existingRow.po_map?.id === poId ? existingRow.po_map : null);
      if (po) {
        next.po = po.id ?? poId;
        next.po_map_id = po.id ?? poId;
        next.po_map = {
          id: po.id ?? poId,
          original_po: po.original_po ?? existingRow.po_map?.original_po,
          internal_po: po.internal_po ?? existingRow.po_map?.internal_po,
          delivery_date: po.delivery_date ?? existingRow.po_map?.delivery_date,
        };
      }
    }

    const styleId = next.style ?? next.style_id ?? existingRow.style ?? existingRow.style_id;
    const previousStyleId = existingRow.style ?? existingRow.style_id;
    const styleChanged =
      styleId != null && previousStyleId != null && String(styleId) !== String(previousStyleId);
    if (styleId != null) {
      const styleEntry = styleList.find((s) => s.style_id === styleId);
      const searchEntry = drawerStyleOptions.find((opt) => opt.value === styleId);
      next.style = styleId;
      next.style_id = styleId;
      if (styleEntry?.style_info) {
        cacheStyleOption(styleId, styleEntry.style_info.style_no);
        next.style_ref = {
          style_no: styleEntry.style_info.style_no,
          description: styleEntry.style_info.description,
          brand_info: styleEntry.style_info.brand_info,
          sub_style_map: styleEntry.style_info.sub_style_map,
        };
        next.style_description = next.style_description ?? styleEntry.style_info.description;
        next.brand = next.brand ?? styleEntry.style_info.brand_info?.name;
      } else if (searchEntry) {
        cacheStyleOption(styleId, searchEntry.label);
        next.style_ref = {
          style_no: searchEntry.label,
          description: searchEntry.description ?? next.style_description,
          brand_info: searchEntry.brand ? { name: searchEntry.brand } : undefined,
        };
        next.style_description = next.style_description ?? searchEntry.description;
        next.brand = next.brand ?? searchEntry.brand;
      } else if (!styleChanged && existingRow.style_ref) {
        next.style_ref = existingRow.style_ref;
      } else {
        next.style_ref = {
          style_no: styleLabelCacheRef.current.get(styleId) || `Style ${styleId}`,
          description: next.style_description,
          brand_info: next.brand ? { name: next.brand } : undefined,
        };
      }
    }

    const salesContractId = next.sales_contract_id ?? existingRow.sales_contract_id;
    if (salesContractId != null) {
      const scOption = salesContractOptions.find((opt) => opt.value === salesContractId);
      if (scOption) {
        next.sales_contract_ref = {
          reference_no: scOption.label ?? existingRow.sales_contract_ref?.reference_no,
        };
      } else if (existingRow.sales_contract_ref) {
        next.sales_contract_ref = existingRow.sales_contract_ref;
      }
    }

    if (next.sub_style_id != null && next.sub_style) {
      cacheSubStyleOption(next.sub_style_id, next.sub_style);
    }

    return next;
  };

  const handleStyleChange = (rowIndex, styleId) => {
    const styleInfo = getSelectedStyleInfo(styleId);
    setSelectedStyles((prev) => ({ ...prev, [rowIndex]: styleId }));
    if (styleInfo) {
      form.setFieldsValue({
        [`breakdown_details.${rowIndex}.brand`]: styleInfo.style_info?.brand_info?.name,
        [`breakdown_details.${rowIndex}.style_description`]: styleInfo.style_info?.description,
        [`breakdown_details.${rowIndex}.sub_style`]: undefined,
        [`breakdown_details.${rowIndex}.sub_style_id`]: undefined,
      });
    } else {
      setSelectedStyles((prev) => ({ ...prev, [rowIndex]: null }));
    }
  };

  const getSubStyleDisplay = (row) => {
    if (row?.sub_style) return row.sub_style;
    if (row?.sub_style_id == null) return "";
    return (
      subStyleLabelCacheRef.current.get(row.sub_style_id) ||
      subStyleSearchOptions.find((opt) => opt.value === row.sub_style_id)?.label ||
      ""
    );
  };

  // Helper functions to get display labels for table
  const getPODisplayLabel = (poId, row) => {
    if (row?.po_map?.original_po) return row.po_map.original_po;
    if (row?.po_map?.internal_po) return row.po_map.internal_po;
    const po = poList.find((p) => p.id === poId);
    return po ? po.original_po || po.internal_po || `PO ${po.id}` : "";
  };

  const getStyleDisplayLabel = (styleId, row) => {
    if (row?.style_ref?.style_no) return row.style_ref.style_no;
    const style = styleList.find((s) => s.style_id === styleId);
    if (style) return style.style_info?.style_no || `Style ${styleId}`;
    return styleLabelCacheRef.current.get(styleId) || drawerStyleOptions.find((opt) => opt.value === styleId)?.label || "";
  };

  const getSizeDisplayLabel = (sizeId, sizeDetail) => {
    if (sizeDetail?.size_name) return sizeDetail.size_name;
    if (sizeId == null) return "—";
    const size = availableSizes.find((s) => sizesMatch(s.value, sizeId));
    return size?.label || `Size ${sizeId}`;
  };

  const materialBreakdownKeyOptions = useMemo(() => {
    const breakdownDetails = getBreakdownRowsSnapshot();
    const seenKeys = new Set();
    const seenBreakdownIds = new Set();
    const opts = [];
    breakdownDetails.forEach((row, rowIndex) => {
      if (rowIsPolyBag(row)) return;
      dedupeSizeDetailsForLink(row.size_details).forEach((sd) => {
        const rowKey = breakdownRowSizeKey(rowIndex, sd.size_id);
        const persistedId = getPersistedBreakdownLineId(sd);
        if (persistedId != null) {
          if (seenBreakdownIds.has(persistedId)) return;
          seenBreakdownIds.add(persistedId);
        } else if (seenKeys.has(rowKey)) {
          return;
        }
        seenKeys.add(rowKey);
        const poLabel = getPODisplayLabel(row.po, row) || "—";
        const styleLabel = getStyleDisplayLabel(row.style, row) || "—";
        const sizeLabel = getSizeDisplayLabel(sd.size_id, sd);
        opts.push({
          value: rowKey,
          label: `Row ${rowIndex + 1}: ${poLabel} / ${styleLabel} / ${sizeLabel}`,
        });
      });
    });
    return opts;
  }, [getBreakdownRowsSnapshot, breakdownTableRevision, poList, styleList, availableSizes]);

  const getColorDisplayLabel = (colorId) => {
    const color = colorList.find((c) => c.id === colorId);
    return color ? color.name || `Color ${colorId}` : "";
  };

  const getShipmentDisplay = (rowData) => {
    if (rowData.shipment_date) {
      const sd = rowData.shipment_date;
      if (moment.isMoment(sd)) return sd.format("YYYY-MM-DD");
      if (typeof sd === "string") {
        return moment(sd, [moment.ISO_8601, "YYYY-MM-DD"], true).isValid() ? moment(sd).format("YYYY-MM-DD") : sd;
      }
      return "";
    }
    if (rowData.po_map?.delivery_date) {
      return moment(rowData.po_map.delivery_date).format("YYYY-MM-DD");
    }
    if (rowData.po) {
      const po = poList.find((p) => p.id === rowData.po);
      if (po?.delivery_date) return moment(po.delivery_date).format("YYYY-MM-DD");
    }
    return "";
  };

  const getOptionDisplayLabel = (value, options) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : "";
  };

  const getMultipleOptionDisplayLabel = (values, options) => {
    if (!values || !Array.isArray(values)) return "";
    return values
      .map((val) => {
        const option = options.find((opt) => opt.value === val);
        return option ? option.label : "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const handlePOChange = (rowIndex, poId) => {
    const poInfo = getSelectedPOInfo(poId);
    if (poInfo) {
      form.setFieldsValue({
        [`breakdown_details.${rowIndex}.style_description`]:
          poInfo.style_description || form.getFieldValue([`breakdown_details.${rowIndex}.style_description`]),
      });
    }
  };

  const handleColorChange = (rowIndex, colorId) => {
    const colorInfo = colorList.find((c) => c.id === colorId);
    if (colorInfo) {
      form.setFieldsValue({
        [`breakdown_details.${rowIndex}.farbe`]: colorInfo.name,
      });
    }
  };

  const handleAddRow = () => {
    const breakdownDetails = form.getFieldValue("breakdown_details") || [];
    seedLookupListsFromBreakdownRows(breakdownDetails);
    setRowDrawerIndex(null);
    setDrawerSelectedSizes([]);
    setDrawerSizeIds([null]);
    setDrawerSalesContractData(null);
    setPolyUnitOptions([]);
  rowDrawerForm.setFieldsValue({
      sales_contract_id: undefined,
      po: undefined,
      shipment_date: undefined,
      style: undefined,
      sub_style: undefined,
      sub_style_id: undefined,
      brand: undefined,
      style_description: undefined,
      color_code: undefined,
      item_type: undefined,
      carton_type: undefined,
      ply: undefined,
      group: undefined,
      poly_type: undefined,
      flap: undefined,
      flap_length: undefined,
      flap_width: undefined,
      gusset: undefined,
      gusset_type: undefined,
      gusset_size: undefined,
      thickness: undefined,
      poly_print_color: undefined,
      air_hole: undefined,
      hanger_cut: undefined,
      hanger_cut_style: undefined,
      hanger_gap_size: undefined,
      poly_perforation: undefined,
      poly_total_qty: 0,
      note: undefined,
      item_no: undefined,
      artikel_nr: undefined,
      model_nr: undefined,
      order_nr: undefined,
      farbe: undefined,
      garment_qty: 0,
      qty_pcs_dzn: 0,
      pack_qty: 1,
      l: 0,
      w: 0,
      h: 0,
      additional_length: 0,
      additional_width: 0,
      au: undefined,
      unit: undefined,
      rate: 0,
      rate_pcs_doz: 0,
      total_cost: 0,
      additional_cost: 0,
      grand_total: 0,
      hanger_cut_size: undefined,
      size_details: [
        {
          size_id: null,
          size_name: "—",
          item: "",
          upc: "",
          garment_qty: 0,
          qty_pcs_dzn: 0,
          pack_qty: 1,
          carton_qty: 0,
          rate: 0,
          rate_pcs_dzn: 0,
          total_cost: 0,
          additional_charge_per_pcs: 0,
          additional_cost: 0,
          grand_total: 0,
          carton_total_qty: 0,
        },
      ],
    });
    syncAllDrawerSizeCalculations(rowDrawerForm.getFieldsValue());
    setRowDrawerOpen(true);
  };

  return (
    <div className="mt-3">
      <style>{`
        .breakdown-table-scroll.custome-table {
          isolation: isolate;
        }
        .breakdown-table-scroll.custome-table table {
          table-layout: auto !important;
        }
        .breakdown-table-scroll.custome-table table tr,
        .breakdown-table-scroll.custome-table table tr:nth-child(even) {
          background-color: transparent !important;
        }
        .breakdown-table-scroll.custome-table table tr:hover,
        .breakdown-table-scroll.custome-table table tr:nth-child(even):hover {
          background-color: transparent !important;
        }
        .breakdown-table-scroll.custome-table .breakdown-sticky-col {
          position: sticky !important;
          background-clip: padding-box !important;
          transform: translateZ(0);
        }
        .breakdown-table-scroll.custome-table thead .breakdown-sticky-col {
          background-color: #cdffcd !important;
          background: #cdffcd !important;
        }
        .breakdown-table-scroll.custome-table thead .breakdown-sticky-col::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: #cdffcd;
          z-index: -1;
        }
        .breakdown-table-scroll.custome-table tbody .breakdown-sticky-col,
        .breakdown-table-scroll.custome-table tfoot .breakdown-sticky-col {
          background-color: #ffffff !important;
          background: #ffffff !important;
        }
        .breakdown-table-scroll.custome-table tbody .breakdown-sticky-col::before,
        .breakdown-table-scroll.custome-table tfoot .breakdown-sticky-col::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: #ffffff;
          z-index: -1;
        }
      `}</style>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 16 }}>
        <Button type="primary" onClick={handleAddRow} disabled={viewMode || !buyerId}>
          Add Row
        </Button>
      </div>

      {/* Add Material button with selected count - only in edit mode */}
      {isEdit && !viewMode && (
        <div style={{ marginBottom: 10 }}>
          <Button
            type="primary"
            onClick={openBulkMaterialModal}
            icon={<PlusCircleOutlined />}
            disabled={!selectedRowKeys.length}
            title={
              !selectedRowKeys.length
                ? "Select at least one carton line in the breakdown table"
                : undefined
            }
          >
            Add / link supporting material
          </Button>
        </div>
      )}

      <Form.List name="breakdown_details">
        {(fields, { add }) => {
          formListAddRef.current = add;

          /**
           * Column definitions per section. Carton table excludes all poly-specific columns.
           * Each column entry: { key, header, minWidth, render(rowData, sizeDetail), headerStyle? }
           */
          const headerCols = (wantPoly) => {
            const cols = [
              { key: "po", header: "PO", minWidth: 150, render: (r) => getPODisplayLabel(r.po, r) || "" },
              ...(wantPoly
                ? []
                : [{ key: "shipment_date", header: "Shipment Date", minWidth: 130, render: (r) => getShipmentDisplay(r) }]),
              { key: "style", header: "Style", minWidth: 150, render: (r) => getStyleDisplayLabel(r.style, r) || "" },
            ];
            if (!wantPoly) {
              cols.push(
                { key: "sub_style", header: "Sub-style", minWidth: 130, render: (r) => getSubStyleDisplay(r) || "" },
                { key: "brand", header: "Brand", minWidth: 120, render: (r) => r.brand || "" },
              );
            }
            cols.push(
              {
                key: "style_description",
                header: "Style Description",
                minWidth: 180,
                render: (r) => r.style_description || "",
              },
              {
                key: "color_code",
                header: "Color/Code",
                minWidth: 130,
                render: (r) =>
                  r.color_code != null && r.color_code !== ""
                    ? getColorDisplayLabel(
                        typeof r.color_code === "number" ? r.color_code : Number(r.color_code) || r.color_code,
                      ) || String(r.color_code)
                    : "",
              },
              {
                key: "item_type",
                header: "Item Type",
                minWidth: 120,
                render: (r) => getItemTypeLabel(r.item_type) || "",
              },
            );
            if (!wantPoly) {
              cols.push(
                {
                  key: "carton_type",
                  header: "Carton Type",
                  minWidth: 220,
                  render: (r) => getOptionDisplayLabel(r.carton_type_id ?? r.carton_type, cartonTypeOptions) || "",
                },
                {
                  key: "ply",
                  header: "Ply",
                  minWidth: 100,
                  render: (r) => getOptionDisplayLabel(r.ply_id ?? r.ply, plyOptions) || "",
                },
              );
            }
            return cols;
          };

          const sizeLevelCols = (wantPoly, cartonBoxOnly = false, polyOnly = false) => {
            const cols = [
              {
                key: "size",
                header: "Size",
                minWidth: 100,
                render: (_r, sd) =>
                  sd.size_id == null
                    ? "—"
                    : sd.size_name || availableSizes.find((s) => sizesMatch(s.value, sd.size_id))?.label || `Size ${sd.size_id}`,
                cellStyle: { fontWeight: "bold" },
              },
            ];
            if (!cartonBoxOnly && !polyOnly) {
              cols.push(
                { key: "item", header: "Item", minWidth: 100, render: (_r, sd) => sd.item || "" },
                { key: "upc", header: "UPC", minWidth: 100, render: (_r, sd) => sd.upc || "" },
              );
            }
            if (!polyOnly) {
              cols.push(
                { key: "garment_qty", header: "Garment Qty", minWidth: 130, render: (_r, sd) => sd.garment_qty || 0 },
              );
            }
            if (!cartonBoxOnly && !polyOnly) {
              cols.push({
                key: "qty_pcs_dzn",
                header: "Qty(Pcs/Dzn)",
                minWidth: 130,
                headerStyle: WRAP_HEADER_STYLE,
                render: (r, sd) =>
                  isCartonBoxItemType(r?.item_type, (v) => getItemTypeLabel(v)) ? "" : sd.qty_pcs_dzn || 0,
              });
            }
            if (!polyOnly) {
              cols.push({
                key: "pack_qty",
                header: wantPoly ? "Pack Qty(Pcs/Dzn)" : "Pack Qty(Pcs)",
                minWidth: 110,
                render: (_r, sd) => sd.pack_qty || 1,
              });
            }
            cols.push(
              {
                key: "carton_total_qty",
                header: wantPoly ? "Poly Total Qty" : "Carton Total Qty",
                minWidth: 140,
                headerStyle: WRAP_HEADER_STYLE,
                render: (r, sd) => formatBreakdownCell(getSizeCartonTotalQty(r, sd, wantPoly)),
              },
            );
            return cols;
          };

          const dimCols = (wantPoly) => {
            const cols = [
              { key: "l", header: "L", minWidth: 90, render: (r) => r.l ?? "" },
              { key: "w", header: "W", minWidth: 90, render: (r) => r.w ?? "" },
            ];
            if (!wantPoly) {
              cols.push({ key: "h", header: "H", minWidth: 90, render: (r) => r.h ?? "" });
            }
            cols.push({
              key: "au",
              header: "A/U",
              minWidth: 100,
              render: (r) => getOptionDisplayLabel(r.au_formula_id ?? r.au, auFormulaOptions) || "",
            });
            return cols;
          };

          const RATE_COST_COLS = (wantPoly) => {
            if (wantPoly) {
              return [
                {
                  key: "rate",
                  header: "Rate (Per LBS)",
                  minWidth: 130,
                  headerStyle: WRAP_HEADER_STYLE,
                  render: (_r, sd) => formatBreakdownCell(sd.rate),
                },
                {
                  key: "additional_charge_per_pcs",
                  header: "Additional Charge",
                  minWidth: 140,
                  headerStyle: WRAP_HEADER_STYLE,
                  render: (_r, sd) => formatBreakdownCell(sd.additional_charge_per_pcs),
                },
                {
                  key: "rate_pcs",
                  header: "Rate/Pcs",
                  minWidth: 120,
                  headerStyle: WRAP_HEADER_STYLE,
                  render: (_r, sd) => formatBreakdownCell(sd.rate_pcs ?? sd.rate_pcs_dzn),
                },
                {
                  key: "rate_dzn",
                  header: "Rate/Dzn",
                  minWidth: 120,
                  headerStyle: WRAP_HEADER_STYLE,
                  render: (_r, sd) => {
                    const pcs = Number(sd.rate_pcs ?? sd.rate_pcs_dzn ?? 0);
                    const charge = Number(sd.additional_charge_per_pcs ?? 0);
                    const withAdd =
                      sd.rate_dzn != null && sd.rate_dzn !== ""
                        ? Number(sd.rate_dzn)
                        : pcs * 12 + charge;
                    return formatBreakdownCell(withAdd);
                  },
                },
                {
                  key: "grand_total",
                  header: "Total Cost",
                  minWidth: 130,
                  render: (_r, sd) =>
                    formatBreakdownCell(
                      sd.grand_total ?? Number(sd.total_cost || 0) + Number(sd.additional_cost || 0),
                    ),
                },
              ];
            }
            return [
              {
                key: "rate",
                header: "Rate (Per SQM)",
                minWidth: 130,
                headerStyle: WRAP_HEADER_STYLE,
                render: (_r, sd) => formatBreakdownCell(sd.rate),
              },
              {
                key: "rate_pcs_dzn",
                header: "Rate/Pcs",
                minWidth: 130,
                headerStyle: WRAP_HEADER_STYLE,
                render: (_r, sd) => formatBreakdownCell(sd.rate_pcs_dzn),
              },
              {
                key: "additional_cost",
                header: "Additional Charge",
                minWidth: 140,
                headerStyle: WRAP_HEADER_STYLE,
                render: (_r, sd) => formatBreakdownCell(sd.additional_cost),
              },
              {
                key: "total_cost",
                header: "Total Cost",
                minWidth: 130,
                render: (_r, sd) => formatBreakdownCell(sd.total_cost),
              },
              {
                key: "grand_total",
                header: "Total",
                minWidth: 120,
                render: (_r, sd) => formatBreakdownCell(sd.grand_total),
              },
            ];
          };

          const POLY_ROW_COLS = [
            {
              key: "poly_type",
              header: "Poly Type",
              minWidth: 140,
              render: (r) => getOptionDisplayLabel(r.poly_type_id ?? r.poly_type, polyTypeOptions) || "",
            },
            { key: "flap", header: "Flap", minWidth: 90, render: (r) => getOptionDisplayLabel(r.flap, YES_NO_OPTIONS) || "" },
            { key: "flap_length", header: "Flap Length", minWidth: 120, render: (r) => r.flap_length ?? "" },
            { key: "gusset", header: "Gusset", minWidth: 95, render: (r) => getOptionDisplayLabel(r.gusset, YES_NO_OPTIONS) || "" },
            {
              key: "gusset_type",
              header: "Gusset Type",
              minWidth: 170,
              render: (r) =>
                getOptionDisplayLabel(
                  r.gusset_type_id ?? (Array.isArray(r.gusset_type) ? r.gusset_type?.[0] : r.gusset_type),
                  gussetTypeOptions,
                ) || "",
            },
            { key: "gusset_size", header: "Gusset Size", minWidth: 120, render: (r) => r.gusset_size ?? "" },
            { key: "thickness", header: "Thickness", minWidth: 110, render: (r) => r.thickness ?? "" },
            {
              key: "poly_print_color",
              header: "Poly Print Color",
              minWidth: 140,
              render: (r) => r.poly_print_color || r.poly_print || "",
            },
            {
              key: "air_hole",
              header: "Air Hole",
              minWidth: 130,
              render: (r) => getOptionDisplayLabel(r.air_hole_type_id ?? r.air_hole, airHoleTypeOptions) || "",
            },
            { key: "hanger_cut", header: "Hanger Cut", minWidth: 120, render: (r) => getOptionDisplayLabel(r.hanger_cut, YES_NO_OPTIONS) || "" },
            { key: "hanger_cut_style", header: "Hanger Cut Style", minWidth: 150, render: (r) => r.hanger_cut_style || "" },
            { key: "hanger_gap_size", header: "Hanger Gap Size", minWidth: 140, render: (r) => r.hanger_gap_size ?? "" },
            { key: "poly_perforation", header: "Poly Perforation", minWidth: 140, render: (r) => r.poly_perforation || "" },
            {
              key: "poly_total_qty_row",
              header: "Poly Total Qty",
              minWidth: 130,
              headerStyle: WRAP_HEADER_STYLE,
              render: (r) => formatBreakdownCell(r.poly_total_qty),
            },
          ];

          const tailCols = (wantPoly) => {
            if (wantPoly) {
              return [];
            }
            return [
              { key: "group", header: "Group", minWidth: 100, render: (r) => r.group || "" },
              { key: "note", header: "Note", minWidth: 110, render: (r) => r.note || "" },
              { key: "item_no", header: "Item No.", minWidth: 110, render: (r) => r.item_no || "" },
              { key: "artikel_nr", header: "ARTIKEL NR. / ARTICLE", minWidth: 170, render: (r) => r.artikel_nr || "" },
              {
                key: "farbe",
                header: "FARBE",
                minWidth: 130,
                render: (r) =>
                  r.farbe ||
                  (r.color_code != null && r.color_code !== ""
                    ? getColorDisplayLabel(
                        typeof r.color_code === "number" ? r.color_code : Number(r.color_code) || r.color_code,
                      ) || ""
                    : ""),
              },
            ];
          };

          const columnsFor = (wantPoly, cartonBoxOnly = false) => {
            if (cartonBoxOnly) {
              return [
                ...headerCols(false),
                ...sizeLevelCols(false, true, false),
                ...dimCols(false),
                ...RATE_COST_COLS(false),
                ...tailCols(false),
              ];
            }
            if (wantPoly) {
              return [
                ...headerCols(true),
                ...POLY_ROW_COLS,
                ...sizeLevelCols(true, false, true),
                ...dimCols(true),
                ...RATE_COST_COLS(true),
              ];
            }
            return [
              ...headerCols(false),
              ...sizeLevelCols(false, false, false),
              ...dimCols(false),
              ...RATE_COST_COLS(false),
              ...tailCols(false),
            ];
          };

          const renderSection = (wantPoly, cartonBoxOnly = false) => {
            const showSelectColumn = !wantPoly && !viewMode;
            const columns = applyStickyThroughStyle(columnsFor(wantPoly, cartonBoxOnly), showSelectColumn);

            const sectionRows = [];
            let serial = 0;
            let hasSectionData = false;
            const breakdownRows = (() => {
              void breakdownTableRevision;
              void breakdownDetailsWatch;
              return getBreakdownRowsSnapshot();
            })();

            const sectionTotals = computeSectionGrandTotals(fields, breakdownRows, {
              wantPoly,
              cartonBoxOnly,
              rowIsPolyBag,
              rowIsCartonBox,
            });

            fields.forEach((field, index) => {
              const rowData = breakdownRows[index] || form.getFieldValue(["breakdown_details", index]) || {};
              if (rowIsPolyBag(rowData) !== wantPoly) return;
              if (cartonBoxOnly && !rowIsCartonBox(rowData)) return;
              if (!wantPoly && !cartonBoxOnly && rowIsCartonBox(rowData)) return;
              const rowSizes = dedupeSizeDetailsForLink(rowData.size_details || []);
              if (!rowSizes.length) return;
              hasSectionData = true;
              rowSizes.forEach((sizeDetail, sizeIndex) => {
                serial += 1;
                const rowKey = breakdownRowSizeKey(index, sizeDetail.size_id);
                const rowBg = getBreakdownRowBg(serial);
                sectionRows.push(
                  <tr key={`${field.key}-${sizeIndex}-${rowKey}-${breakdownTableRevision}-${wantPoly ? "p" : "c"}`}>
                    {showSelectColumn && (
                      <td
                        className="t-body breakdown-sticky-col"
                        style={{
                          textAlign: "center",
                          ...stickyPinnedCellStyle({
                            left: 0,
                            zIndex: STICKY_BODY_Z_BASE,
                          }),
                        }}
                      >
                        <Checkbox
                          checked={selectedRowKeys.includes(rowKey)}
                          onChange={(e) => handleSelectRow(rowKey, e.target.checked)}
                        />
                      </td>
                    )}
                    <td
                      className="t-body breakdown-sticky-col"
                      style={{
                        textAlign: "center",
                        ...stickyPinnedCellStyle({
                          left: showSelectColumn ? CHECKBOX_COL_WIDTH : 0,
                          zIndex: STICKY_BODY_Z_BASE + 1,
                        }),
                      }}
                    >
                      {serial}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`t-body ${col.stickyLeft != null ? "breakdown-sticky-col" : "breakdown-scroll-col"}`}
                        style={
                          col.stickyLeft != null ? stickyColStyle(col) : scrollableCellStyle(col, rowBg)
                        }
                      >
                        {col.render(rowData, sizeDetail)}
                      </td>
                    ))}
                    {!viewMode && (
                      <td
                        className="t-body breakdown-scroll-col"
                        style={{ ...scrollableCellStyle({}, rowBg), whiteSpace: "nowrap" }}
                      >
                        <EditOutlined
                          style={{ fontSize: 16, color: "#1890ff", cursor: "pointer" }}
                          onClick={() => openRowDrawer(index)}
                        />
                        {sizeIndex === 0 && (
                          <CopyOutlined
                            style={{ fontSize: 16, color: "#1890ff", cursor: "pointer", marginLeft: 8 }}
                            title="Copy row"
                            onClick={() => handleCopyBreakdownRow(index)}
                          />
                        )}
                      </td>
                    )}
                  </tr>,
                );
              });
            });

            const totalCols = columns.length + 1 + (showSelectColumn ? 1 : 0) + (viewMode ? 0 : 1);
            if (!sectionRows.length) {
              sectionRows.push(
                <tr key={`empty-${wantPoly ? "poly" : cartonBoxOnly ? "carton-box" : "carton"}`}>
                  <td colSpan={totalCols} style={{ textAlign: "center", padding: "16px" }}>
                    No {wantPoly ? "poly bag" : cartonBoxOnly ? "carton box" : "carton"} lines yet.
                  </td>
                </tr>,
              );
            }

            const checkboxHeaderStyle = {
              minWidth: CHECKBOX_COL_WIDTH,
              textAlign: "center",
              ...stickyPinnedCellStyle({
                left: 0,
                zIndex: STICKY_HEADER_Z_BASE,
                background: BREAKDOWN_HEADER_BG,
              }),
            };
            const slHeaderStyle = {
              minWidth: SL_COL_WIDTH,
              ...stickyPinnedCellStyle({
                left: showSelectColumn ? CHECKBOX_COL_WIDTH : 0,
                zIndex: STICKY_HEADER_Z_BASE + 1,
                background: BREAKDOWN_HEADER_BG,
              }),
            };

            return (
              <div className="custome-table breakdown-table-scroll" style={{ overflowX: "auto", fontSize: 11, lineHeight: 1.1 }}>
                <table
                  style={{
                    minWidth: wantPoly ? "5200px" : "3800px",
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                  }}
                >
                  <thead>
                    <tr>
                      {showSelectColumn && (
                        <th className="bc-java breakdown-sticky-col" style={checkboxHeaderStyle}>
                          <Checkbox
                            onChange={handleSelectAll}
                            checked={
                              getVisibleRowIndices().length > 0 &&
                              selectedRowKeys.length === getVisibleRowIndices().length
                            }
                            indeterminate={
                              selectedRowKeys.length > 0 &&
                              selectedRowKeys.length < getVisibleRowIndices().length
                            }
                          />
                        </th>
                      )}
                      <th className="bc-java breakdown-sticky-col" style={slHeaderStyle}>
                        SL
                      </th>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={
                            col.stickyLeft != null ? "bc-java breakdown-sticky-col" : "bc-java breakdown-scroll-col"
                          }
                          style={{
                            minWidth: col.minWidth,
                            ...(col.headerStyle || {}),
                            ...(col.stickyLeft != null
                              ? stickyColStyle(col, { isHeader: true })
                              : scrollableCellStyle(col, BREAKDOWN_HEADER_BG)),
                          }}
                        >
                          {col.header}
                        </th>
                      ))}
                      {!viewMode && (
                        <th
                          className="bc-java breakdown-scroll-col"
                          style={{ minWidth: 120, ...scrollableCellStyle({}, BREAKDOWN_HEADER_BG) }}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>{sectionRows}</tbody>
                  {hasSectionData && (
                    <tfoot>
                      <tr>
                        {showSelectColumn && (
                          <td
                            className="t-body breakdown-sticky-col"
                            style={stickyPinnedCellStyle({
                              left: 0,
                              zIndex: STICKY_BODY_Z_BASE,
                            })}
                          />
                        )}
                        <td
                          className="t-body breakdown-sticky-col"
                          style={stickyPinnedCellStyle({
                            left: showSelectColumn ? CHECKBOX_COL_WIDTH : 0,
                            zIndex: STICKY_BODY_Z_BASE + 1,
                          })}
                        />
                        {columns.map((col) => (
                          <td
                            key={`total-${col.key}`}
                            className={`t-body ${col.stickyLeft != null ? "breakdown-sticky-col" : "breakdown-scroll-col"}`}
                            style={
                              col.stickyLeft != null
                                ? stickyColStyle(col, { extra: { fontWeight: "bold" } })
                                : scrollableCellStyle(col, BREAKDOWN_ROW_BG_ODD, { fontWeight: "bold" })
                            }
                          >
                            {renderSectionGrandTotalCell(col, sectionTotals)}
                          </td>
                        ))}
                        {!viewMode && (
                          <td
                            className="t-body breakdown-scroll-col"
                            style={scrollableCellStyle({}, BREAKDOWN_ROW_BG_ODD)}
                          />
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            );
          };

          // Determine which sections to render based on existing rows so we only show
          // the table(s) that actually have data.
          const sectionBreakdownRows = (() => {
            void breakdownTableRevision;
            void breakdownDetailsWatch;
            return getBreakdownRowsSnapshot();
          })();
          const hasPolyRow = sectionBreakdownRows.some((row) => rowIsPolyBag(row));
          const hasCartonBoxRow = sectionBreakdownRows.some((row) => row && !rowIsPolyBag(row) && rowIsCartonBox(row));
          const hasRegularCartonRow = sectionBreakdownRows.some(
            (row) => row && !rowIsPolyBag(row) && !rowIsCartonBox(row),
          );

          return (
            <>
              {fields.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  No data. Click &quot;Add Row&quot; to add breakdown details.
                </div>
              )}
              {hasCartonBoxRow && (
                <>
                  <h4 style={{ marginTop: 12, marginBottom: 8 }}>Carton box breakdown</h4>
                  {renderSection(false, true)}
                </>
              )}
              {hasRegularCartonRow && (
                <>
                  <h4 style={{ marginTop: hasCartonBoxRow ? 24 : 12, marginBottom: 8 }}>Carton breakdown</h4>
                  {renderSection(false, false)}
                </>
              )}
              {hasPolyRow && (
                <>
                  <h4 style={{ marginTop: 24, marginBottom: 8 }}>Poly bag breakdown</h4>
                  {renderSection(true, false)}
                </>
              )}
            </>
          );
        }}
      </Form.List>

      {/* Material Details Section */}
      {materialDetailsList.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 12 }}>Material Details</h4>
          <div className="custome-table">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="bc-java" style={{ minWidth: 50 }}>
                    SL
                  </th>
                  <th className="bc-java" style={{ minWidth: 140 }}>
                    Material Type
                  </th>
                  <th className="bc-java" style={{ minWidth: 100 }}>
                    Ply
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Material Qty
                  </th>
                  <th className="bc-java" style={{ minWidth: 100 }}>
                    A/U
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    L
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    H
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    W
                  </th>
                  <th className="bc-java" style={{ minWidth: 100 }}>
                    Rate
                  </th>
                  <th className="bc-java" style={{ minWidth: 130, ...WRAP_HEADER_STYLE }}>
                    Rate/Pcs
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Total Cost
                  </th>
                  {!viewMode && (
                    <th className="bc-java" style={{ minWidth: 80 }}>
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {materialDetailsList.map((mat, idx) => {
                  const matTypeLabel =
                    getOptionDisplayLabel(mat.material_type, supportingMaterialTypeOptions) || mat.material_type || "";
                  const matPlyLabel = getOptionDisplayLabel(mat.material_ply, plyOptions) || mat.material_ply || "";
                  const matAuLabel = getOptionDisplayLabel(mat.material_au, auFormulaOptions) || mat.material_au || "";
                  return (
                    <tr key={mat.id}>
                      <td className="t-body" style={{ textAlign: "center" }}>
                        {idx + 1}
                      </td>
                      <td className="t-body">{matTypeLabel}</td>
                      <td className="t-body">{matPlyLabel}</td>
                      <td className="t-body">{mat.material_qty ?? ""}</td>
                      <td className="t-body">{matAuLabel}</td>
                      <td className="t-body">{mat.material_l ?? ""}</td>
                      <td className="t-body">{mat.material_h ?? ""}</td>
                      <td className="t-body">{mat.material_w ?? ""}</td>
                      <td className="t-body">{mat.material_rate ?? ""}</td>
                      <td className="t-body">{mat.material_rate_pcs_doz ?? ""}</td>
                      <td className="t-body">{mat.material_total_cost ?? ""}</td>
                      {!viewMode && (
                        <td className="t-body" style={{ textAlign: "center" }}>
                          <DeleteOutlined
                            style={{ fontSize: 16, color: "red", cursor: "pointer" }}
                            onClick={() => handleDeleteMaterial(mat.id)}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Supporting Materials (Saved from API) */}
      {isEdit && !viewMode && apiSupportingMaterials?.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 12 }}>Supporting Materials</h4>
          <div className="custome-table">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="bc-java" style={{ minWidth: 50 }}>
                    SL
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    BreakDown SL no
                  </th>
                  <th className="bc-java" style={{ minWidth: 160 }}>
                    Material Type
                  </th>
                  <th className="bc-java" style={{ minWidth: 100 }}>
                    Ply
                  </th>
                  <th className="bc-java" style={{ minWidth: 100 }}>
                    A/U
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Material Qty
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    L
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    W
                  </th>
                  <th className="bc-java" style={{ minWidth: 80 }}>
                    H
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Rate (Per SQM)
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Rate/Pcs
                  </th>
                  <th className="bc-java" style={{ minWidth: 130 }}>
                    Total Cost
                  </th>
                  <th className="bc-java" style={{ minWidth: 120 }}>
                    Id
                  </th>
                  {!viewMode && (
                    <th className="bc-java" style={{ minWidth: 120 }}>
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {apiSupportingMaterials.map((mat, idx) => {
                  const typeLabel =
                    getOptionDisplayLabel(mat.material_type_id, supportingMaterialTypeOptions) ||
                    mat.material_type_id ||
                    "";
                  const plyLabel = getOptionDisplayLabel(mat.ply_id, plyOptions) || mat.ply_id || "";
                  const auLabel =
                    getOptionDisplayLabel(mat.a_u_formula_id, auFormulaOptions) || mat.a_u_formula_id || "";
                  const relationRows = mat?.supporting_detail_relations || mat?.breakdowns || [];
                  const breakdownDetails = form.getFieldValue("breakdown_details") || [];
                  const lineSerialMap = buildCartonLineSerialMap(breakdownDetails, rowIsPolyBag, rowIsCartonBox);

                  const bdSLNos = dedupeTargetKeys(
                    (relationRows || [])
                      .map((b) => lineSerialMap.get(b.booking_carton_break_down_id))
                      .filter((sl) => sl != null),
                  );
                  const bdIds = dedupeTargetKeys(
                    (relationRows || []).map((b) => b.booking_carton_break_down_id).filter(Boolean),
                  );

                  return (
                    <tr key={mat.id || idx}>
                      <td className="t-body" style={{ textAlign: "center" }}>
                        {idx + 1}
                      </td>
                      <td className="t-body">{bdSLNos.length ? bdSLNos.join(", ") : ""}</td>
                      <td className="t-body">{typeLabel}</td>
                      <td className="t-body">{plyLabel}</td>
                      <td className="t-body">{auLabel}</td>
                      <td className="t-body">{mat.material_qty ?? ""}</td>
                      <td className="t-body">{mat.length ?? ""}</td>
                      <td className="t-body">{mat.width ?? ""}</td>
                      <td className="t-body">{mat.height ?? ""}</td>
                      <td className="t-body">{mat.rate ?? ""}</td>
                      <td className="t-body">{mat.rate_per_qty ?? ""}</td>
                      <td className="t-body">{mat.total_amount ?? ""}</td>
                      <td className="t-body">{bdIds.length ? bdIds.join(", ") : ""}</td>
                      {!viewMode && (
                        <td className="t-body" style={{ whiteSpace: "nowrap" }}>
                          <EditOutlined
                            style={{ fontSize: 16, color: "#1890ff", cursor: "pointer", marginRight: 8 }}
                            onClick={() => handleEditSupportingMaterial(mat)}
                          />
                          <Button type="link" size="small" onClick={() => handleCopySupportingMaterial(mat)}>
                            Copy
                          </Button>
                          <Popconfirm
                            title="Are you sure you want to delete this supporting material?"
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => handleApiDeleteSupportingMaterial(mat)}
                          >
                            <DeleteOutlined style={{ fontSize: 16, color: "red", cursor: "pointer" }} />
                          </Popconfirm>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Drawer
        title="Edit Breakdown Details"
        open={rowDrawerOpen}
        onClose={closeRowDrawer}
        width={1120}
        destroyOnClose
        bodyStyle={{
          paddingBottom: 80,
          maxHeight: "calc(100vh - 108px)",
          overflowY: "auto",
        }}
        maskClosable={false}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button onClick={() => closeRowDrawer({ discard: true })} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleRowDrawerSubmit}>
              Save
            </Button>
          </div>
        }
      >
        <Form
          form={rowDrawerForm}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            syncAllDrawerSizeCalculations(allValues);
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="item_type"
                label="Item Type"
                rules={[{ required: true, message: "Please select item type" }]}
              >
                <Select
                  placeholder="Select"
                  options={itemTypeOptions}
                  onChange={handleDrawerItemTypeChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sales_contract_id"
                label="Sales Contract"
                rules={[{ required: true, message: "Please select sales contract" }]}
              >
                <Select
                  placeholder="Select sales contract"
                  allowClear
                  showSearch
                  filterOption={false}
                  disabled={!buyerId}
                  onSearch={debouncedFetchSalesContracts}
                  onChange={(val) => {
                    rowDrawerForm.setFieldsValue({
                      sales_contract_id: val,
                      po: undefined,
                      style: undefined,
                      style_id: undefined,
                      sub_style: undefined,
                      sub_style_id: undefined,
                      brand: undefined,
                      style_description: undefined,
                      shipment_date: undefined,
                      color_code: undefined,
                    });
                    setDrawerPoOptions([]);
                    setDrawerStyleOptions([]);
                    setSubStyleSearchOptions([]);
                    fetchDrawerSalesContractData(val);
                    fetchGarmentQtyForDrawer({ sales_contract_id: val, po: undefined, style: undefined });
                  }}
                  options={salesContractOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="po" label="PO">
                <Select
                  placeholder="Search PO"
                  allowClear
                  showSearch
                  filterOption={false}
                  disabled={!drawerSalesContractId}
                  onSearch={debouncedFetchDrawerPoList}
                  onClear={() => fetchDrawerPoList("", drawerSalesContractId)}
                  onChange={(val) => {
                    const poInfo = getSelectedPOInfo(val);
                    const delivery = poInfo?.delivery_date;
                    rowDrawerForm.setFieldsValue({
                      po: val,
                      shipment_date: delivery
                        ? moment(delivery, "YYYY-MM-DD", true).isValid()
                          ? moment(delivery, "YYYY-MM-DD")
                          : moment(delivery)
                        : undefined,
                    });
                    fetchGarmentQtyForDrawer({ po: val });
                  }}
                  options={drawerPoOptions.map((p) => ({
                    value: p.id,
                    label: p.original_po || p.internal_po || `PO ${p.id}`,
                  }))}
                />
              </Form.Item>
            </Col>
            {!drawerItemIsPoly && (
              <Col span={8}>
                <Form.Item name="shipment_date" label="Shipment Date">
                  <CustomDatePicker style={{ width: "100%" }} size="small" disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="style" label="Style">
                <Select
                  placeholder="Search style"
                  allowClear
                  showSearch
                  filterOption={false}
                  optionFilterProp="label"
                  onSearch={debouncedFetchStylesForDrawer}
                  onClear={() => fetchStylesForDrawer("")}
                  onChange={(val, option) => {
                    applyDrawerStyleSelection(val, option);
                    fetchGarmentQtyForDrawer({ style: val });
                  }}
                  options={drawerStyleOptions}
                />
              </Form.Item>
            </Col>
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="sub_style_id" label="Sub-style">
                  <Select
                    placeholder="Search sub-style"
                    allowClear
                    showSearch
                    filterOption={false}
                    optionFilterProp="label"
                    onSearch={debouncedFetchSubStylesForDrawer}
                    onClear={() => fetchSubStylesForDrawer("")}
                    onChange={(val, option) => {
                      const subStyleNo = option?.label ?? "";
                      cacheSubStyleOption(val, subStyleNo);
                      rowDrawerForm.setFieldsValue({
                        sub_style_id: val,
                        sub_style: subStyleNo,
                      });
                      fetchGarmentQtyForDrawer({ sub_style_id: val, sub_style: subStyleNo });
                    }}
                    options={subStyleSearchOptions}
                  />
                </Form.Item>
                <Form.Item name="sub_style" hidden>
                  <Input type="hidden" />
                </Form.Item>
              </Col>
            )}
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="brand" label="Brand">
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="style_description" label="Style Description">
                <Input placeholder="As per selected PO" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color_code" label="Color/Code">
                <Input placeholder="Color/Code" />
              </Form.Item>
            </Col>
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="carton_type" label="Carton Type">
                  <Select
                    placeholder="Select"
                    allowClear
                    showSearch
                    filterOption={false}
                    onSearch={fetchCartonTypes}
                    options={cartonTypeOptions}
                  />
                </Form.Item>
              </Col>
            )}
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="ply" label="Ply">
                  <Select
                    placeholder="Select"
                    allowClear
                    showSearch
                    filterOption={false}
                    onSearch={fetchPlies}
                    options={plyOptions}
                  />
                </Form.Item>
              </Col>
            )}
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="group" label="Group">
                  <Input />
                </Form.Item>
              </Col>
            )}
            {getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <>
                <Col span={8}>
                  <Form.Item name="poly_type" label="Poly Type">
                    <Select
                      placeholder="Select"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchPolyTypes}
                      options={polyTypeOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="flap" label="Flap">
                    <Select placeholder="Yes / No" allowClear options={YES_NO_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="flap_length" label="Flap Length">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      defaultValue={0}
                      placeholder="Flap Length"
                      onChange={() => {
                        syncFlapDerivedDimensions();
                        queueDrawerRecalculation(true);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="flap_width" label="Flap Width">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      defaultValue={0}
                      placeholder="Flap Width"
                      onChange={() => {
                        syncFlapDerivedDimensions();
                        queueDrawerRecalculation(true);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="gusset" label="Gusset">
                    <Select placeholder="Yes / No" allowClear options={YES_NO_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="gusset_type" label="Gusset Type">
                    <Select
                      placeholder="Select"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchGussetTypes}
                      options={gussetTypeOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="gusset_size" label="Gusset Size">
                    <InputNumber style={{ width: "100%" }} min={0} defaultValue={0} placeholder="Gusset Size" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="thickness" label="Thickness">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      defaultValue={0}
                      placeholder="Thickness"
                      onChange={() => queueDrawerRecalculation(true)}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="poly_print_color" label="Poly Print Color">
                    <Input placeholder="Poly Print Color" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="air_hole" label="Air Hole">
                    <Select
                      placeholder="Select"
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={fetchAirHoleTypes}
                      options={airHoleTypeOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hanger_cut" label="Hanger Cut">
                    <Select placeholder="Yes / No" allowClear options={YES_NO_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hanger_cut_style" label="Hanger Cut Style">
                    <Input placeholder="Hanger Cut Style" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hanger_gap_size" label="Hanger Gap Size">
                    <InputNumber style={{ width: "100%" }} min={0} defaultValue={0} placeholder="Hanger Gap Size" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="poly_perforation" label="Poly Perforation">
                    <Input placeholder="Poly Perforation" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="poly_total_qty" label="Poly Total Qty" initialValue={0}>
                    <InputNumber style={{ width: "100%" }} min={0} disabled />
                  </Form.Item>
                </Col>
              </>
            )}
            {!drawerItemIsPoly && (
              <>
                <Col span={8}>
                  <Form.Item name="note" label="Note">
                    <Input placeholder="Note" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="item_no" label="Item No.">
                    <Input placeholder="Item No." />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="artikel_nr" label="ARTIKEL NR. / ARTICLE">
                    <Input placeholder="ARTIKEL NR. / ARTICLE" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="farbe" label="FARBE">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </>
            )}
            {/* <Col span={8}>
              <Form.Item name="garment_qty" label="Garment Qty">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col> */}
            {/* <Col span={8}>
              <Form.Item name="qty_pcs_dzn" label="Qty(Pcs/Dzn)">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col> */}
            {/* <Col span={8}>
              <Form.Item name="pack_qty" label="Pack Qty">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col> */}
            <Col span={8}>
              <Form.Item name="l" label="L" initialValue={0}>
                <InputNumber style={{ width: "100%" }} min={0} onChange={() => queueDrawerRecalculation(drawerItemIsPoly)} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="w" label="W" initialValue={0}>
                <InputNumber style={{ width: "100%" }} min={0} onChange={() => queueDrawerRecalculation(drawerItemIsPoly)} />
              </Form.Item>
            </Col>
            {!getItemTypeLabel(drawerItemType).toLowerCase().includes("poly") && (
              <Col span={8}>
                <Form.Item name="h" label="H" initialValue={0}>
                  <InputNumber style={{ width: "100%" }} min={0} onChange={() => queueDrawerRecalculation(drawerItemIsPoly)} />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item
                name="au"
                label={
                  drawerItemIsPoly
                    ? "A/U (LBS / KGS)"
                    : drawerItemIsCarton
                      ? "A/U (INCH / CM / MM)"
                      : "A/U"
                }
              >
                <Select
                  placeholder="Select"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={fetchAuFormulas}
                  options={getAllowedAuOptions(drawerItemType)}
                  onChange={() => queueDrawerRecalculation(drawerItemIsPoly)}
                />
              </Form.Item>
            </Col>
            {!drawerItemIsCartonBox && drawerItemIsPoly && (
              <Col span={8}>
                <Form.Item name="unit" label="Unit">
                  <Select
                    placeholder="Select unit"
                    allowClear
                    showSearch
                    options={polyUnitOptions}
                    onChange={() => queueDrawerRecalculation(true)}
                  />
                </Form.Item>
              </Col>
            )}
            {drawerItemIsPoly && (
              <>
                <Col span={8}>
                  <Form.Item name="additional_width" label="Additional Width">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      onChange={() => queueDrawerRecalculation(true)}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="additional_length" label="Additional Length">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      onChange={() => queueDrawerRecalculation(true)}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            {/* <Col span={8}>
              <Form.Item name="rate" label="Rate">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
              </Form.Item>
            </Col> */}
            {/* <Col span={8}>
              <Form.Item name="rate_pcs_doz" label="Rate/Pcs/Doz">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
              </Form.Item>
            </Col> */}
            {/* <Col span={8}>
              <Form.Item name="total_cost" label="Total Cost">
                <InputNumber style={{ width: "100%" }} min={0} disabled />
              </Form.Item>
            </Col> */}
          </Row>

          <div style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item label="Select Sizes" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    placeholder="Select sizes for this row"
                    style={{ width: "100%" }}
                    value={drawerSelectedSizes}
                    onChange={handleDrawerSizeSelection}
                    disabled={viewMode || !availableSizes.length}
                    options={availableSizes}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ marginTop: 24 }}>
            <h4 style={{ marginBottom: 16 }}>Size Details</h4>
            <div className="custome-table" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th className="bc-java">Size</th>
                    {!drawerItemIsCartonBox && !drawerItemIsPoly && <th className="bc-java">Item</th>}
                    {!drawerItemIsCartonBox && !drawerItemIsPoly && <th className="bc-java">UPC</th>}
                    {!drawerItemIsPoly && <th className="bc-java">Garment Qty</th>}
                    {!drawerItemIsCartonBox && !drawerItemIsPoly && (
                      <th className="bc-java" style={WRAP_HEADER_STYLE}>
                        Qty(Pcs/Dzn)
                      </th>
                    )}
                    {!drawerItemIsPoly && (
                      <th className="bc-java">
                        {drawerItemIsCartonBox ? "Pack Qty(Pcs)" : "Pack Qty"}
                      </th>
                    )}
                    <th className="bc-java">
                      {drawerItemIsPoly ? "Poly Total Qty" : drawerItemIsCartonBox ? "Carton Total Qty" : "Carton Qty"}
                    </th>
                    <th className="bc-java">Rate</th>
                    <th className="bc-java" style={WRAP_HEADER_STYLE}>
                      {drawerItemIsPoly ? "Additional Charge" : "Additional Charge / Pcs"}
                    </th>
                    {drawerItemIsPoly ? (
                      <>
                        <th className="bc-java" style={WRAP_HEADER_STYLE}>
                          Rate/Pcs
                        </th>
                        <th className="bc-java" style={WRAP_HEADER_STYLE}>
                          Rate/Dzn
                        </th>
                      </>
                    ) : (
                      <th className="bc-java" style={WRAP_HEADER_STYLE}>
                        {drawerItemIsCartonBox ? "Rate/Pcs" : "Rate/Pcs/Dzn"}
                      </th>
                    )}
                    {!drawerItemIsPoly && (
                      <th className="bc-java" style={WRAP_HEADER_STYLE}>
                        Additional Charge
                      </th>
                    )}
                    <th className="bc-java">Total Cost</th>
                    {!drawerItemIsPoly && <th className="bc-java">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  <Form.List name="size_details">
                    {(sizeFields) => (
                      <>
                        {sizeFields.map((sizeField) => {
                          const sizeDetail = rowDrawerForm.getFieldValue(["size_details", sizeField.name]) || {};
                          const sizeId = sizeDetail.size_id;
                          const sizeInfo = sizeId != null ? availableSizes.find((s) => s.value === sizeId) : null;
                          const sizeLabel =
                            sizeId == null
                              ? "—"
                              : sizeInfo?.label || sizeDetail.size_name || `Size ${sizeId}`;
                          return (
                            <tr key={sizeField.key}>
                              <td className="t-body" style={{ fontWeight: "bold" }}>
                                {sizeLabel}
                                <Form.Item name={[sizeField.name, "size_id"]} hidden>
                                  <Input type="hidden" />
                                </Form.Item>
                              </td>
                              {!drawerItemIsCartonBox && !drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item name={[sizeField.name, "item"]} style={{ marginBottom: 0 }} noStyle>
                                    <Input placeholder="Item" size="small" />
                                  </Form.Item>
                                </td>
                              )}
                              {!drawerItemIsCartonBox && !drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item name={[sizeField.name, "upc"]} style={{ marginBottom: 0 }} noStyle>
                                    <Input placeholder="UPC" size="small" />
                                  </Form.Item>
                                </td>
                              )}
                              {!drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "garment_qty"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Garment Qty"
                                      size="small"
                                      min={0}
                                      style={{ width: "100%" }}
                                      onChange={() => queueDrawerRecalculation()}
                                    />
                                  </Form.Item>
                                </td>
                              )}
                              {!drawerItemIsCartonBox && !drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "qty_pcs_dzn"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder="Qty(Pcs/Dzn)"
                                      size="small"
                                      min={0}
                                      style={{ width: "100%" }}
                                      onChange={() => queueDrawerRecalculation()}
                                    />
                                  </Form.Item>
                                </td>
                              )}
                              {!drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "pack_qty"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                  <InputNumber
                                    placeholder="Pack Qty"
                                    size="small"
                                    min={1}
                                    style={{ width: "100%" }}
                                    onChange={() => queueDrawerRecalculation()}
                                  />
                                </Form.Item>
                              </td>
                              )}
                              <td className="t-body">
                                <Form.Item
                                  name={[sizeField.name, "carton_qty"]}
                                  style={{ marginBottom: 0 }}
                                  noStyle
                                >
                                  <InputNumber
                                    placeholder="Carton Qty"
                                    size="small"
                                    min={0}
                                    style={{ width: "100%" }}
                                    onChange={(value) => {
                                      if (value == null) {
                                        rowDrawerForm.setFieldValue(
                                          ["size_details", sizeField.name, "manual_carton_qty"],
                                          false,
                                        );
                                        queueDrawerRecalculation();
                                        return;
                                      }
                                      rowDrawerForm.setFieldValue(
                                        ["size_details", sizeField.name, "manual_carton_qty"],
                                        true,
                                      );
                                      setTimeout(() => {
                                        syncDrawerSizeCalculations(
                                          sizeField.name,
                                          rowDrawerForm.getFieldsValue(),
                                          { carton_qty: Number(value) || 0 },
                                        );
                                      }, 0);
                                    }}
                                  />
                                </Form.Item>
                              </td>
                              <td className="t-body">
                                <Form.Item
                                  name={[sizeField.name, "rate"]}
                                  style={{ marginBottom: 0 }}
                                  noStyle
                                >
                                  <InputNumber
                                    placeholder="Rate"
                                    size="small"
                                    min={0}
                                    step={0.0001}
                                    precision={BREAKDOWN_DECIMALS}
                                    style={{ width: "100%" }}
                                    onChange={() => queueDrawerRecalculationForSize(sizeField.name)}
                                  />
                                </Form.Item>
                              </td>
                              <td className="t-body">
                                <Form.Item
                                  name={[sizeField.name, "additional_charge_per_pcs"]}
                                  style={{ marginBottom: 0 }}
                                  noStyle
                                >
                                  <InputNumber
                                    size="small"
                                    min={0}
                                    step={0.0001}
                                    precision={BREAKDOWN_DECIMALS}
                                    style={{ width: "100%" }}
                                    onChange={() => queueDrawerRecalculation()}
                                  />
                                </Form.Item>
                              </td>
                              {drawerItemIsPoly ? (
                                <>
                                  <td className="t-body">
                                    <Form.Item
                                      name={[sizeField.name, "rate_pcs"]}
                                      style={{ marginBottom: 0 }}
                                      noStyle
                                    >
                                      <InputNumber
                                        placeholder="Rate/Pcs"
                                        size="small"
                                        min={0}
                                        step={0.0001}
                                        precision={BREAKDOWN_DECIMALS}
                                        style={{ width: "100%" }}
                                        onChange={(value) => handlePolyRatePcsChange(sizeField.name, value)}
                                      />
                                    </Form.Item>
                                  </td>
                                  <td className="t-body">
                                    <Form.Item
                                      name={[sizeField.name, "rate_dzn"]}
                                      style={{ marginBottom: 0 }}
                                      noStyle
                                    >
                                      <InputNumber
                                        placeholder="Rate/Dzn"
                                        size="small"
                                        min={0}
                                        step={0.0001}
                                        precision={BREAKDOWN_DECIMALS}
                                        style={{ width: "100%" }}
                                        onChange={(value) => handlePolyRateDznChange(sizeField.name, value)}
                                      />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "rate_pcs_dzn"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                  </td>
                                </>
                              ) : (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "rate_pcs_dzn"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      placeholder={drawerItemIsCartonBox ? "Rate/Pcs" : "Rate/Pcs/Dzn"}
                                      size="small"
                                      min={0}
                                      step={0.0001}
                                      precision={BREAKDOWN_DECIMALS}
                                      style={{ width: "100%" }}
                                      disabled={drawerItemIsCarton}
                                      onChange={
                                        !drawerItemIsCarton
                                          ? () => {
                                              setTimeout(() => {
                                                const cartonQty = rowDrawerForm.getFieldValue([
                                                  "size_details",
                                                  sizeField.name,
                                                  "carton_qty",
                                                ]);
                                                const ratePcsDzn = rowDrawerForm.getFieldValue([
                                                  "size_details",
                                                  sizeField.name,
                                                  "rate_pcs_dzn",
                                                ]);
                                                if (cartonQty != null && ratePcsDzn != null) {
                                                  const totalCost = roundBreakdownMoney(
                                                    Number(cartonQty) * Number(ratePcsDzn),
                                                  );
                                                  rowDrawerForm.setFieldValue(
                                                    ["size_details", sizeField.name, "total_cost"],
                                                    totalCost,
                                                  );
                                                  syncDrawerSizeGrandTotal(sizeField.name, {
                                                    ...rowDrawerForm.getFieldsValue(),
                                                    size_details: (
                                                      rowDrawerForm.getFieldValue("size_details") || []
                                                    ).map((sd, i) =>
                                                      i === sizeField.name ? { ...sd, total_cost: totalCost } : sd,
                                                    ),
                                                  });
                                                }
                                              }, 0);
                                            }
                                          : undefined
                                      }
                                    />
                                  </Form.Item>
                                </td>
                              )}
                              {!drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "additional_cost"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      size="small"
                                      min={0}
                                      disabled
                                      readOnly
                                      step={0.0001}
                                      precision={BREAKDOWN_DECIMALS}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </td>
                              )}
                              <td className="t-body">
                                {drawerItemIsPoly ? (
                                  <>
                                    <Form.Item
                                      name={[sizeField.name, "grand_total"]}
                                      style={{ marginBottom: 0 }}
                                      noStyle
                                    >
                                      <InputNumber
                                        size="small"
                                        min={0}
                                        disabled
                                        readOnly
                                        precision={BREAKDOWN_DECIMALS}
                                        style={{ width: "100%" }}
                                      />
                                    </Form.Item>
                                    {/* Keep payload fields mounted but hidden for poly */}
                                    <Form.Item name={[sizeField.name, "additional_cost"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "total_cost"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "garment_qty"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "pack_qty"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "qty_pcs_dzn"]} hidden>
                                      <InputNumber />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "item"]} hidden>
                                      <Input />
                                    </Form.Item>
                                    <Form.Item name={[sizeField.name, "upc"]} hidden>
                                      <Input />
                                    </Form.Item>
                                  </>
                                ) : (
                                  <Form.Item
                                    name={[sizeField.name, "total_cost"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      size="small"
                                      min={0}
                                      disabled
                                      readOnly
                                      precision={BREAKDOWN_DECIMALS}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                )}
                              </td>
                              {!drawerItemIsPoly && (
                                <td className="t-body">
                                  <Form.Item
                                    name={[sizeField.name, "grand_total"]}
                                    style={{ marginBottom: 0 }}
                                    noStyle
                                  >
                                    <InputNumber
                                      size="small"
                                      min={0}
                                      disabled
                                      readOnly
                                      precision={BREAKDOWN_DECIMALS}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                </tbody>
              </table>
            </div>
          </div>

        </Form>
      </Drawer>

      <Modal
        title="Carton Supporting Material"
        open={materialModalOpen}
        onCancel={closeMaterialModal}
        onOk={handleMaterialSave}
        okText="Save"
        destroyOnClose
      >
        <Form
          form={materialForm}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            syncSupportingMaterialCalculations(allValues);
          }}
          initialValues={{
            material_qty: 0,
            material_l: 0,
            material_h: 0,
            material_w: 0,
            material_rate: 0,
            material_rate_pcs_doz: 0,
            material_total_cost: 0,
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="material_type" label="Material Type">
                <Select
                  placeholder="Select"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={fetchSupportingMaterialTypes}
                  onChange={(materialType) => {
                    syncMaterialQtyFromTargets(materialType);
                  }}
                  options={supportingMaterialTypeOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_ply" label="Ply">
                <Select
                  placeholder="Select"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={fetchPlies}
                  options={plyOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_qty" label="Material Qty">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  onChange={() => syncSupportingMaterialCalculations(materialForm.getFieldsValue())}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_au" label="A/U (INCH / CM / MM)">
                <Select
                  placeholder="Select"
                  allowClear
                  showSearch
                  filterOption={false}
                  onSearch={fetchAuFormulas}
                  onChange={() => {
                    setTimeout(() => {
                      syncSupportingMaterialCalculations(materialForm.getFieldsValue());
                    }, 0);
                  }}
                  options={auFormulaOptions}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="material_target_keys"
                label="Link to carton sizes / rows (multi-select)"
                extra="Supporting material applies to carton lines only — poly rows are excluded."
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="Pick carton breakdown lines, or use table checkboxes above"
                  optionFilterProp="label"
                  options={materialBreakdownKeyOptions}
                  onChange={(keys) => {
                    const uniqueKeys = dedupeTargetKeys(keys);
                    if (uniqueKeys.length !== (keys || []).length) {
                      materialForm.setFieldsValue({ material_target_keys: uniqueKeys });
                    }
                    const materialType = materialForm.getFieldValue("material_type");
                    const qty = resolveMaterialQtyFromGarment(uniqueKeys, materialType);
                    materialForm.setFieldsValue({ material_qty: qty });
                    syncSupportingMaterialCalculations({ ...materialForm.getFieldsValue(), material_qty: qty });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="material_l" label="L">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  onChange={() => syncSupportingMaterialCalculations(materialForm.getFieldsValue())}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="material_h" label="H">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  onChange={() => syncSupportingMaterialCalculations(materialForm.getFieldsValue())}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="material_w" label="W">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  onChange={() => syncSupportingMaterialCalculations(materialForm.getFieldsValue())}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_rate" label="Rate (Per SQM)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.0001}
                  precision={BREAKDOWN_DECIMALS}
                  onChange={() => syncSupportingMaterialCalculations(materialForm.getFieldsValue())}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_rate_pcs_doz" label="Rate/Pcs">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.0001}
                  precision={BREAKDOWN_DECIMALS}
                  disabled
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="material_total_cost" label="Total Cost">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={BREAKDOWN_DECIMALS}
                  disabled
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
